import React, { useState, useEffect, useRef, useMemo } from "react";
import { formatAppDistanceToNow } from "../utils/appDateTime";
import ReactDOM from "react-dom";
import AssignmentDetailModal from "../components/AssignmentDetailModal";
import AssignToSimilarModal from "./AssignToSimilarModal";
import UnassignShiftDialog from "./UnassignShiftDialog";
import CarpoolEditModal from "./CarpoolEditModal";
import { BASE_URL } from "../config";
import API from "../api";
import {
  FaSearch,
  FaSlidersH,
  FaFlag,
  FaCar,
  FaMale,
  FaFemale,
} from "react-icons/fa";
import { AtlasRefreshIconButton } from "./list-toolbar";
import { MdDoNotDisturbOff } from "react-icons/md";
import StaffFilterPanel from "./StaffFilterPanel";
import StaffAvatarCircle from "./StaffAvatarCircle";
import ActionDropdown from "./ActionDropdown";
import { showError, showSuccess, showToast } from "../utils/notifications";
import {
  shiftRowHasTravel,
  travelPayRateSuffix,
} from "../utils/shiftTravelDisplay";
import { formatLegacyScheduleDurationText } from "../utils/scheduleDurationUtils";
import { deriveModalStatusForForm } from "./ShiftDetailModal";
import {
  matchesPersonnelSearch,
  sortPersonnelAlphabetically,
} from "../utils/personnelSearchUtils";

const SHIFT_UI_STATUS_LABELS = {
  unassigned: "Unassigned",
  tentative: "Tentative",
  confirmed: "Confirmed",
  cancelled: "Cancelled",
  declined: "Unfilled",
  deleted: "Deleted",
};

function formatShiftUiStatusLabel(status) {
  const key = String(status ?? "unassigned")
    .toLowerCase()
    .trim();
  return SHIFT_UI_STATUS_LABELS[key] || key;
}

function unassignReasonLabel(code) {
  const c = String(code || "").toLowerCase();
  if (c === "sick") return "Sick";
  if (c === "mia") return "MIA";
  return "Other";
}

function formatUnassignAgo(modifyDate) {
  return formatAppDistanceToNow(modifyDate);
}

const UNASSIGN_ACTUALS_SUBMITTED_MSG =
  'Attention - Shift Actuals have been submitted. You must change actuals for the assignment to "No Show" before you can unassign the account from the shift';

function isActualSubmittedForUnassignGuard(rawShiftLike) {
  const s = String(
    rawShiftLike?.actualState ??
      rawShiftLike?.actual_state ??
      rawShiftLike?.chosenActualState ??
      "",
  )
    .trim()
    .toLowerCase();
  return (
    s === "as_scheduled" || s === "asscheduled" || s === "different"
  );
}

/** Schedule rows omit actual_state; fetch Jobs shift detail when local payload is empty. */
async function shiftBlocksUnassignBySubmittedActuals(shiftId, localRaw, authHeaders) {
  if (isActualSubmittedForUnassignGuard(localRaw)) return true;
  if (!shiftId) return false;
  try {
    const resp = await fetch(`${BASE_URL}/api/jobs/shifts/${shiftId}`, {
      credentials: "include",
      headers: { ...authHeaders },
    });
    if (!resp.ok) return false;
    const j = await resp.json().catch(() => null);
    const detail = j?.data ?? j ?? {};
    return isActualSubmittedForUnassignGuard(detail);
  } catch {
    return false;
  }
}

/** Same id as POST /api/shifts/:id/assign (slot-aware when Schedule merges parallel shifts). */
function resolveSchedulePanelShiftId(shift, entry, assignment) {
  const fromSlot =
    assignment?.sourceShiftId ?? assignment?.raw?.sourceShiftId;
  if (fromSlot !== undefined && fromSlot !== null && fromSlot !== "") {
    return fromSlot;
  }
  const candidates = [
    shift?.shift_id,
    shift?.id,
    shift?.shiftId,
    shift?.shift_id_string,
    entry?.shift_id,
    entry?.id,
    entry?.shiftId,
    entry?.shift_id_string,
    assignment?.raw?.shift_id,
    assignment?.raw?.id,
    assignment?.raw?.shiftId,
    assignment?.raw?.shift_id_string,
  ];
  for (const c of candidates) {
    if (c !== undefined && c !== null && c !== "") return c;
  }
  return null;
}

/** Raw shift row for the active parallel slot (Schedule merged rows). */
function resolveSlotRawShiftForPanel(entry, assignment, shift) {
  const base = entry?.rawShift || entry?.raw || shift?.rawShift || null;
  const slotId =
    assignment?.sourceShiftId ?? assignment?.raw?.sourceShiftId ?? null;
  if (
    slotId === undefined ||
    slotId === null ||
    slotId === "" ||
    !Array.isArray(entry?.parallelShifts) ||
    entry.parallelShifts.length === 0
  ) {
    return base;
  }
  const found = entry.parallelShifts.find(
    (p) =>
      p &&
      p.id != null &&
      slotId != null &&
      Number(p.id) === Number(slotId),
  );
  return found?.rawShift || base;
}

function resolvePanelShiftPositionMeta(panelShiftDetail, context, entry, shift) {
  const shiftObj = panelShiftDetail || entry?.rawShift || entry || shift || {};
  const positionId =
    shiftObj?.position_id ??
    shiftObj?.positionId ??
    shiftObj?.position?.id ??
    context?.position_id ??
    context?.positionId ??
    null;
  const positionLabel = String(
    shiftObj?.position_name ||
      shiftObj?.positionName ||
      shiftObj?.position ||
      context?.role ||
      context?.position ||
      context?.job_role ||
      entry?.position ||
      entry?.assignments?.[0]?.role ||
      "",
  ).trim();
  return {
    positionId:
      positionId === undefined || positionId === null || positionId === ""
        ? null
        : positionId,
    positionLabel: positionLabel || null,
  };
}

function resolvePanelShiftClientId(panelShiftDetail, context, entry, shift) {
  const shiftObj = panelShiftDetail || entry?.rawShift || entry || shift || {};
  const clientId =
    shiftObj?.client_id ??
    shiftObj?.clientId ??
    context?.client_id ??
    context?.clientId ??
    entry?.client_id ??
    entry?.clientId ??
    shift?.client_id ??
    shift?.clientId ??
    null;
  if (clientId === undefined || clientId === null || clientId === "") return null;
  return clientId;
}

/** YYYY-MM-DD from shift start for legacy week utilization on /api/personnel/temp. */
function utilizationAnchorYmdFromShiftObj(shiftObj) {
  if (!shiftObj || typeof shiftObj !== "object") return "";
  const raw =
    shiftObj.start_date ||
    shiftObj.startDate ||
    shiftObj.shift_start ||
    shiftObj.shiftStart ||
    null;
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : "";
}

/** Map /api/personnel/temp utilization fields to display strings (legacy temp-staff list). */
function formatTempStaffUtilizationFromApi(a) {
  const maxH =
    a.max_hours != null && a.max_hours !== ""
      ? Number(a.max_hours)
      : NaN;
  if (!Number.isFinite(maxH) || maxH <= 0) {
    return { utilization: "0%", hours: "0h/0h" };
  }
  const pct = Number(a.utilization);
  const worked = Number(a.utilization_shift_duration_hours_total);
  const pctRounded = Number.isFinite(pct) ? Math.round(pct) : 0;
  const workedRounded = Number.isFinite(worked)
    ? Math.round(worked * 10) / 10
    : 0;
  const maxRounded = Math.round(maxH * 10) / 10;
  return {
    utilization: `${pctRounded}%`,
    hours: `${workedRounded}h/${maxRounded}h`,
  };
}

function formatTempStaffPayRateFromApi(staff, targetPositionId = null) {
  const rates = Array.isArray(staff?.staff_pay_rates)
    ? staff.staff_pay_rates
    : [];
  if (rates.length === 0) return "";

  const normalizedTargetPositionId =
    targetPositionId === undefined || targetPositionId === null || targetPositionId === ""
      ? null
      : String(targetPositionId);

  const pickRate = (row) => {
    const rawRate =
      row?.custom_pay_rate ?? row?.pay_rate ?? row?.rate ?? row?.amount ?? null;
    const num = Number(rawRate);
    if (!Number.isFinite(num)) return "";
    return `$${num.toFixed(2)}/h`;
  };

  const match =
    (normalizedTargetPositionId &&
      rates.find(
        (row) =>
          String(row?.position_id ?? row?.positionId ?? "") ===
            normalizedTargetPositionId &&
          String(row?.state || "").toLowerCase() !== "deleted",
      )) ||
    // Global "ALL positions" rate (no position) applies when no exact position rate exists
    (normalizedTargetPositionId &&
      rates.find(
        (row) =>
          (row?.position_id ?? row?.positionId ?? null) === null &&
          (row?.client_id ?? null) === null &&
          String(row?.state || "").toLowerCase() !== "deleted",
      )) ||
    rates.find((row) => String(row?.state || "").toLowerCase() === "active") ||
    rates[0];

  return pickRate(match);
}

/** Same merge as Personnel.calculateReliabilityMetrics — reliability_score + utilization metrics. */
async function fetchAccountMetricsBundle(
  accountId,
  authHeaders,
  period = 30,
  utilizationAnchor = "",
) {
  try {
    const metricsParams = new URLSearchParams();
    metricsParams.set("period", String(period));
    if (utilizationAnchor) metricsParams.set("utilization", utilizationAnchor);
    const [reliabilityRes, metricsRes] = await Promise.all([
      fetch(`${BASE_URL}/api/personnel/accounts/${accountId}/reliability`, {
        headers: { ...authHeaders },
        credentials: "include",
      }),
      fetch(
        `${BASE_URL}/api/personnel/accounts/${accountId}/metrics?${metricsParams.toString()}`,
        {
          headers: { ...authHeaders },
          credentials: "include",
        },
      ),
    ]);
    const reliabilityData = await reliabilityRes.json().catch(() => ({}));
    const metricsData = await metricsRes.json().catch(() => ({}));
    const reliabilityScore =
      reliabilityRes.ok && reliabilityData?.success
        ? reliabilityData?.data?.reliability_score
        : null;
    const metricPayload =
      metricsRes.ok && metricsData?.success ? metricsData.data || {} : {};
    return {
      ...metricPayload,
      reliability:
        typeof reliabilityScore === "number"
          ? reliabilityScore
          : metricPayload.reliability ?? 0,
    };
  } catch (err) {
    console.error("Failed to load account metrics bundle", accountId, err);
    return { reliability: 0, utilization: 0 };
  }
}

function parseReliabilityPercent(val) {
  if (typeof val === "number" && Number.isFinite(val)) return val;
  return parseFloat(String(val || "").replace(/[^0-9.]/g, "")) || 0;
}

function readShiftAssignmentFlags(raw) {
  if (!raw || typeof raw !== "object") {
    return {
      ignoreAvailability: false,
      notifyStaffChange: false,
      carpoolName: "",
      carpoolNote: "",
    };
  }
  const ignore =
    Number(raw.ignore_availability) === 1 || raw.ignoreAvailability === true;
  const notify =
    raw.notify_staff_change === 1 ||
    raw.notify_staff_change === true ||
    raw.notifyStaffChange === true;
  const carpoolName = String(raw.carpool_name ?? raw.carpoolName ?? "").trim();
  const carpoolNote = String(raw.carpool_note ?? raw.carpoolNote ?? "").trim();
  return {
    ignoreAvailability: !!ignore,
    notifyStaffChange: !!notify,
    carpoolName,
    carpoolNote,
  };
}

function assignmentRowMatchesSlot(a, activeSlotShiftId, entry) {
  if (activeSlotShiftId == null || activeSlotShiftId === "") return true;
  const aid =
    a?.sourceShiftId ??
    a?.raw?.sourceShiftId ??
    entry?.id ??
    entry?.shift_id;
  return aid != null && Number(aid) === Number(activeSlotShiftId);
}

function isRealAssignedRow(a) {
  const emp = a?.employee || a?.emp || null;
  if (!emp) return false;
  const id =
    emp?.account_id ||
    emp?.id ||
    a?.account_id ||
    a?.accountId ||
    a?.employee_id ||
    null;
  if (!id) return false;
  const state = (a?.status || a?.state || "").toString().toLowerCase();
  if (state === "unassigned" || state === "unassigned_history") return false;
  const email = emp?.email || a?.email || "";
  if (email.toLowerCase() === "unassigned@local.internal") return false;
  const firstName = emp?.first_name || "";
  const lastName = emp?.last_name || "";
  if (firstName === "Unassigned" && lastName === "Account") return false;
  return true;
}

const ShiftDetailPanel = ({
  shift,
  entry,
  assignment,
  isClosedWeek,
  onOpenPersonnelModal,
  onOpenShiftModal,
  onScheduleRefresh,
  /** When set (e.g. Schedule), awaited after assign / status changes so the grid and panel sync before continuing. */
  onScheduleRefreshAwait,
  /** Optional: called after shift-level temp/admin notes are saved (e.g. Jobs panel ↔ modal sync). */
  onShiftNotesSaved,
  /** Schedule: do not open Jobs shift modal after assign; one toast + grid refresh only. */
  suppressShiftModalAfterAssign = false,
  /** Schedule: no shift write permission — assignment flags read-only except when already set (legacy). */
  readOnly = false,
  /** Optional: bump this (e.g. Schedule's refreshTick) to re-fetch the shift detail this panel shows -- picks up edits (like gender) made via the edit modal, which don't otherwise change activeSlotShiftId/entry identity. */
  refreshTick,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterFocusField, setFilterFocusField] = useState(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showSortByDropdown, setShowSortByDropdown] = useState(false);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [carpoolModalOpen, setCarpoolModalOpen] = useState(false);
  const [carpoolSaving, setCarpoolSaving] = useState(false);
  const [positions, setPositions] = useState([]);
  // Initialize filters with division_ids and skills from entry/shift if available
  const getInitialFilters = () => {
    const shiftObj = entry?.rawShift || entry || shift || {};
    const divisionId =
      shiftObj?.division_id ||
      shiftObj?.divisionId ||
      shiftObj?.division ||
      null;
    const requiredSkills = Array.isArray(shiftObj?.required_skills)
      ? shiftObj.required_skills
          .map((s) =>
            typeof s === "string"
              ? s.trim() === ""
                ? null
                : Number(s)
              : Number(s),
          )
          .filter(Boolean)
      : [];
    const division_ids = divisionId ? [Number(divisionId)] : [];

    return {
      types: [],
      minProximity: "",
      maxProximity: "",
      ignoreAvailability: false,
      sortBy: "Preference",
      sortDirection: "Ascending",
      division_ids: division_ids,
      skills: requiredSkills,
    };
  };

  const [appliedFilters, setAppliedFilters] = useState(getInitialFilters());

  // assignment: if present, this is a subcard (position/assignment) detail
  // If assignment is present, prefer to show its details over entry/shift

  // Helper to get the correct context (assignment or entry)
  const context = assignment ? assignment.raw || assignment : entry;
  const panelScheduleDurationText = useMemo(() => {
    const raw =
      resolveSlotRawShiftForPanel(entry, assignment, shift) ||
      entry?.rawShift ||
      entry?.raw ||
      shift?.rawShift ||
      shift ||
      null;
    return formatLegacyScheduleDurationText(raw);
  }, [entry, assignment, shift]);
  const contextEmp =
    assignment && assignment.emp
      ? assignment.emp
      : context?.employee || context?.emp || null;
  const staffAccountId =
    (assignment &&
      (assignment.employee?.account_id ||
        assignment.account_id ||
        assignment.employee?.id)) ||
    (contextEmp && (contextEmp.account_id || contextEmp.id)) ||
    null;

  // When an assignment is provided, only show THAT assignment in the
  // Assignment section. Otherwise, show all entry.assignments.
  const visibleAssignments = useMemo(() => {
    if (assignment) {
      if (
        assignment.isUnassignedHistory ||
        assignment.status === "unassigned_history"
      ) {
        return [];
      }
      const base = assignment.raw || assignment;
      const emp =
        assignment.emp ||
        base.employee ||
        base.account ||
        base.assignee ||
        base.assigned_account ||
        base.user ||
        base.person ||
        base.temp ||
        null;

      // Normalize status so that either explicit status/state or a
      // confirmed flag (boolean/int) from the schedule query results
      // in a clean "confirmed" / "pending" label.
      const rawStatus = assignment.status || base.status || base.state || "";
      const isConfirmedFlag =
        assignment.confirmed === true ||
        assignment.confirmed === 1 ||
        base.confirmed === true ||
        base.confirmed === 1;
      const normalizedStatus =
        rawStatus && typeof rawStatus === "string"
          ? rawStatus.toLowerCase()
          : isConfirmedFlag
            ? "confirmed"
            : "pending";

      return [
        {
          ...base,
          employee: emp,
          status: normalizedStatus,
        },
      ];
    }

    return entry?.assignments || [];
  }, [assignment, entry]);

  // Filter to only REAL assignments (exclude placeholder unassigned slots)
  const realAssignments = useMemo(() => {
    return (visibleAssignments || []).filter((a) => {
      const emp = a?.employee || a?.emp || null;
      // If no employee object at all, it's a placeholder
      if (!emp) return false;

      const id =
        emp?.account_id ||
        emp?.id ||
        a?.account_id ||
        a?.accountId ||
        a?.employee_id ||
        null;
      // Exclude if no valid account_id
      if (!id) return false;

      // Exclude if state is explicitly 'unassigned' or history-only row
      const state = (a?.status || a?.state || "").toString().toLowerCase();
      if (state === "unassigned" || state === "unassigned_history") return false;

      // Exclude if email is the placeholder account email
      const email = emp?.email || a?.email || "";
      if (email.toLowerCase() === "unassigned@local.internal") return false;

      // Exclude if name suggests it's a placeholder
      const firstName = emp?.first_name || "";
      const lastName = emp?.last_name || "";
      if (firstName === "Unassigned" && lastName === "Account") return false;

      return true;
    });
  }, [visibleAssignments]);

  const slotRawShift = useMemo(
    () => resolveSlotRawShiftForPanel(entry, assignment, shift),
    [entry, assignment, shift],
  );

  // Per parallel slot — merged entry.travel_time_* is only from the first shift in the group.
  const sidebarTravelAmountRaw =
    slotRawShift?.travel_time_amount ?? shift?.travel_time_amount ?? null;
  const sidebarTravelTypeRaw =
    slotRawShift?.travel_time_type ?? shift?.travel_time_type ?? null;
  const sidebarHasTravel = shiftRowHasTravel(
    sidebarTravelAmountRaw,
    sidebarTravelTypeRaw,
  );
  const assignmentFlags = useMemo(
    () => readShiftAssignmentFlags(slotRawShift),
    [slotRawShift],
  );

  const activeSlotShiftId = useMemo(() => {
    const fromSlot =
      assignment?.sourceShiftId ?? assignment?.raw?.sourceShiftId;
    if (fromSlot !== undefined && fromSlot !== null && fromSlot !== "") {
      return fromSlot;
    }
    return entry?.shift_id ?? entry?.id ?? null;
  }, [assignment, entry]);

  /** Capacity for the physical shift behind the opened slot (merged rows sum requiredStaff across parallels). */
  const requiredSlotsForActiveShift = useMemo(() => {
    if (Array.isArray(entry?.parallelShifts) && activeSlotShiftId != null) {
      const p = entry.parallelShifts.find(
        (x) => x && Number(x.id) === Number(activeSlotShiftId),
      );
      if (p && Number(p.requiredStaff) > 0) return Number(p.requiredStaff);
    }
    return Math.max(
      1,
      typeof entry?.requiredStaff === "number" && entry.requiredStaff > 0
        ? entry.requiredStaff
        : 1,
    );
  }, [entry, activeSlotShiftId]);

  /** Assignees on this physical shift only (fixes merged-card capacity vs API). */
  const slotRealAssignments = useMemo(() => {
    const list = entry?.assignments || [];
    return list.filter(
      (a) =>
        assignmentRowMatchesSlot(a, activeSlotShiftId, entry) &&
        isRealAssignedRow(a),
    );
  }, [entry, activeSlotShiftId]);

  /** Real assignees across the full merged time-slot block (all parallel sourceShiftId rows). */
  const mergedBlockAssignedAccountKeys = useMemo(() => {
    const keys = new Set();
    const list = entry?.assignments || [];
    for (const a of list) {
      if (!isRealAssignedRow(a)) continue;
      const emp = a?.employee || a?.emp || null;
      const rawId =
        emp?.account_id ??
        emp?.id ??
        a?.account_id ??
        a?.accountId ??
        a?.employee_id ??
        null;
      if (rawId == null || rawId === "") continue;
      keys.add(String(rawId));
    }
    return keys;
  }, [entry?.assignments]);

  // Helper: detect if there is at least one REAL assigned staff member.
  // (Schedule view includes placeholder "unassigned" slots as entries with employee=null.)
  const hasRealAssignedStaff = useMemo(() => {
    // Use the same filtering logic as realAssignments
    return realAssignments.length > 0;
  }, [realAssignments]);

  const panelShiftId = useMemo(() => {
    const sid =
      assignment?.sourceShiftId ??
      assignment?.raw?.sourceShiftId ??
      entry?.shift_id ??
      entry?.id ??
      shift?.shift_id ??
      shift?.id;
    const n = Number(sid);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [
    assignment?.sourceShiftId,
    assignment?.raw?.sourceShiftId,
    entry?.shift_id,
    entry?.id,
    shift?.shift_id,
    shift?.id,
  ]);

  const isUnassignedSlotView = !!entry?._isUnassignedSlotView;

  const panelInstanceKey = useMemo(() => {
    const assignPart = assignment
      ? [
          assignment?.sourceShiftId ?? assignment?.raw?.sourceShiftId ?? "",
          assignment?.employee?.account_id ??
            assignment?.account_id ??
            assignment?.emp?.account_id ??
            "unassigned",
        ].join(":")
      : isUnassignedSlotView
        ? "unassigned-slot"
        : "entry-level";
    return `${activeSlotShiftId ?? "none"}:${assignPart}`;
  }, [
    activeSlotShiftId,
    assignment?.sourceShiftId,
    assignment?.raw?.sourceShiftId,
    assignment?.employee?.account_id,
    assignment?.account_id,
    assignment?.emp?.account_id,
    isUnassignedSlotView,
  ]);

  const buildFiltersForShift = () => {
    const shiftObj = entry?.rawShift || entry || shift || {};
    const divisionId =
      shiftObj?.division_id ||
      shiftObj?.divisionId ||
      shiftObj?.division ||
      null;
    const requiredSkills = Array.isArray(shiftObj?.required_skills)
      ? shiftObj.required_skills
          .map((s) =>
            typeof s === "string"
              ? s.trim() === ""
                ? null
                : Number(s)
              : Number(s),
          )
          .filter(Boolean)
      : [];
    return {
      types: [],
      minProximity: "",
      maxProximity: "",
      ignoreAvailability: false,
      sortBy: "Preference",
      sortDirection: "Ascending",
      division_ids: divisionId ? [Number(divisionId)] : [],
      skills: requiredSkills,
    };
  };

  /** Loaded from same Jobs shift detail API as ShiftDetailModal. */
  const [panelShiftDetail, setPanelShiftDetail] = useState(null);

  /** Same status values as ShiftDetailModal status dropdown. */
  const shiftAssignmentStatus = useMemo(() => {
    if (panelShiftDetail) {
      return deriveModalStatusForForm(panelShiftDetail);
    }
    return "unassigned";
  }, [panelShiftDetail]);

  /** Prefer Jobs shift detail API (parent position fallback); then schedule row context. */
  const panelPositionLabel = useMemo(() => {
    const fromApi = String(panelShiftDetail?.position_name ?? "").trim();
    if (fromApi) return fromApi;
    const shiftObj = entry?.rawShift || entry || shift || {};
    const label = String(
      context?.role ||
        context?.position ||
        context?.job_role ||
        entry?.position ||
        shiftObj?.positionName ||
        shiftObj?.position_name ||
        entry?.assignments?.[0]?.role ||
        "",
    ).trim();
    return label || null;
  }, [panelShiftDetail, context, entry, shift]);
  const panelRequiredGender = useMemo(() => {
    const shiftObj = entry?.rawShift || entry || shift || {};
    return String(
      panelShiftDetail?.gender ?? shiftObj?.gender ?? "",
    )
      .trim()
      .toLowerCase();
  }, [panelShiftDetail, entry, shift]);
  const panelShiftPositionMeta = useMemo(
    () => resolvePanelShiftPositionMeta(panelShiftDetail, context, entry, shift),
    [panelShiftDetail, context, entry, shift],
  );
  const panelShiftClientId = useMemo(
    () => resolvePanelShiftClientId(panelShiftDetail, context, entry, shift),
    [panelShiftDetail, context, entry, shift],
  );

  // Allow actions (Confirm, Unassign, Assign to Similar) even after confirmation.
  // We no longer disable the ACTIONS button based on assignment status.
  const isActionsDisabled = !!isClosedWeek;

  // Close dropdown when clicking outside or pressing ESC
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionDropdown && !event.target.closest(".relative")) {
        setShowActionDropdown(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape" && showActionDropdown) {
        setShowActionDropdown(false);
      }
    };

    if (showActionDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showActionDropdown]);

  // Live staff data fetched from personnel search API
  const STAFF_PAGE_SIZE = 20;
  const [staffData, setStaffData] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffPage, setStaffPage] = useState(1);
  const [staffTotalCount, setStaffTotalCount] = useState(0);
  const [staffTotalPages, setStaffTotalPages] = useState(1);
  const searchTimer = useRef(null);
  const staffSearchSeqRef = useRef(0);
  const [clientName, setClientName] = useState("");
  const [metricsMap, setMetricsMap] = useState({});
  const [outsourcedPayers, setOutsourcedPayers] = useState([]);
  const [selectedStaffTab, setSelectedStaffTab] = useState("temp");
  const [showConfirmCreate, setShowConfirmCreate] = useState(false);
  const [showCreateTempModal, setShowCreateTempModal] = useState(false);
  const [showEditNoteModal, setShowEditNoteModal] = useState(false);
  const [showEditAdminModal, setShowEditAdminModal] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [adminDraft, setAdminDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [localNote, setLocalNote] = useState(null);
  const [localAdminNote, setLocalAdminNote] = useState(null);
  const [filterPanelMode, setFilterPanelMode] = useState("temp");
  const [outsourcedSortBy, setOutsourcedSortBy] = useState("Outsourced Agency");
  const [outsourcedSortDirection, setOutsourcedSortDirection] =
    useState("Ascending");
  const [showAssignSimilarModal, setShowAssignSimilarModal] = useState(false);
  const [modalOriginalShift, setModalOriginalShift] = useState(null);
  const [scheduleUnassignOpen, setScheduleUnassignOpen] = useState(false);
  const [scheduleUnassignPayload, setScheduleUnassignPayload] = useState({
    shiftId: null,
    accountId: null,
    staffDisplayName: "",
    startAt: null,
    endAt: null,
    silentUnassignSuccess: false,
  });
  const [assignedStaffPhones, setAssignedStaffPhones] = useState({});
  const [assignedStaffRecruiters, setAssignedStaffRecruiters] = useState({});
  const pendingAssignAfterUnassignRef = useRef(null);

  // Reset panel-local state when user opens a different shift slot / assignment.
  useEffect(() => {
    staffSearchSeqRef.current += 1;
    setSearchTerm("");
    setStaffPage(1);
    setStaffData([]);
    setStaffTotalCount(0);
    setStaffTotalPages(1);
    setSelectedStaffTab("temp");
    setAppliedFilters(buildFiltersForShift());
    setShowFilterPanel(false);
  }, [panelInstanceKey]);

  // Auto-apply division and required skills from the opened shift so
  // the staff table shows matching temp staff by default.
  useEffect(() => {
    const shiftObj = entry?.rawShift || entry || shift || {};

    // Debug: Check for division_id in various formats
    const foundDivisionId =
      shiftObj?.division_id ||
      shiftObj?.divisionId ||
      shiftObj?.division ||
      shiftObj?.["sr.division_id"] ||
      null;
    if (!foundDivisionId) {
      console.log(
        "ShiftDetailPanel - No division_id found in shiftObj. Available keys:",
        Object.keys(shiftObj),
      );
    }
    const requiredSkills = Array.isArray(shiftObj?.required_skills)
      ? shiftObj.required_skills
          .map((s) =>
            typeof s === "string"
              ? s.trim() === ""
                ? null
                : Number(s)
              : Number(s),
          )
          .filter(Boolean)
      : [];

    const division_ids = foundDivisionId ? [Number(foundDivisionId)] : [];
    console.log("Auto-applying filters:", {
      divisionId: foundDivisionId,
      division_ids,
      requiredSkills,
      shiftObj,
    });

    // Load positions based on division (or all positions if no division)
    loadPositionsForShift(foundDivisionId);

    setAppliedFilters((prev) => {
      // Only update if values actually changed to avoid unnecessary re-renders
      const divsChanged =
        JSON.stringify(prev.division_ids || []) !==
        JSON.stringify(division_ids);
      const skillsChanged =
        JSON.stringify(prev.skills || []) !== JSON.stringify(requiredSkills);
      if (divsChanged || skillsChanged) {
        return { ...prev, division_ids, skills: requiredSkills };
      }
      return prev;
    });
  }, [entry, shift, assignment]);

  // Load positions for the shift's division (or all positions as fallback)
  const loadPositionsForShift = async (divisionId) => {
    try {
      // Reuse the same positions endpoint as the Schedule filters.
      // Backend currently exposes positions via /api/schedules/positions,
      // not /api/positions, so calling the old path caused 404.
      const response = await API.get(`/api/schedules/positions`);
      const positionsData = response.data.data || [];
      console.log(
        "Loaded positions for shift:",
        positionsData.length,
        "positions",
      );
      setPositions(positionsData);
    } catch (error) {
      console.error("Error loading positions for shift:", error);
      setPositions([]);
    }
  };

  const fetchOutsourcedPayers = async () => {
    try {
      // Only hide outsourced payers when there is a REAL assignment (not placeholder unassigned slots)
      // BUT always show them when viewing an unassigned slot
      if (hasRealAssignedStaff && !entry?._isUnassignedSlotView) {
        setOutsourcedPayers([]);
        return;
      }
      // Fetch only outsourced payers
      const resp = await fetch(
        `${BASE_URL}/api/personnel/payers?workerType=outsourced`,
        { headers: { ...getAuthHeaders() } },
      );
      const j = await resp.json().catch(() => null);
      const list = Array.isArray(j)
        ? j
        : j && Array.isArray(j.data)
          ? j.data
          : [];
      // Additional filter for safety (in case backend doesn't filter)
      const filtered = list.filter((p) => {
        const workerType = p.worker_type || "";
        const state = p.state || "active";
        return (
          workerType === "outsourced" && (state === "active" || state == null)
        );
      });
      setOutsourcedPayers(filtered);
      console.log(
        "Loaded outsourced payers:",
        filtered.length || list.length,
        "payers",
      );
    } catch (e) {
      console.error("Failed to load outsourced payers", e);
      setOutsourcedPayers([]);
    }
  };

  const getAuthToken = () =>
    localStorage.getItem("authToken") || localStorage.getItem("token");
  const getAuthHeaders = () => {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Same shift detail source as ShiftDetailModal (/api/jobs/shifts/:id).
  useEffect(() => {
    const sid = activeSlotShiftId;
    if (sid == null || sid === "") {
      setPanelShiftDetail(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${BASE_URL}/api/jobs/shifts/${sid}`, {
          credentials: "include",
          headers: { ...getAuthHeaders() },
        });
        if (!resp.ok) {
          if (!cancelled) setPanelShiftDetail(null);
          return;
        }
        const j = await resp.json().catch(() => null);
        if (!cancelled) setPanelShiftDetail(j?.data ?? j ?? null);
      } catch {
        if (!cancelled) setPanelShiftDetail(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activeSlotShiftId,
    entry?.id,
    entry?.shiftStatus,
    entry?.displayStatus,
    refreshTick,
  ]);

  const sanitizePhone = (raw) => {
    if (!raw) return "";
    const digits = String(raw).replace(/[^0-9]/g, "");
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    return digits ? `+${digits}` : "";
  };

  useEffect(() => {
    const accountIds = Array.from(
      new Set(
        realAssignments
          .map(
            (assignment) =>
              assignment?.employee?.account_id ||
              assignment?.account_id ||
              assignment?.employee?.id ||
              assignment?.id ||
              null,
          )
          .filter(Boolean)
          .map(String),
      ),
    );

    const missingIds = accountIds.filter(
      (accountId) =>
        typeof assignedStaffPhones[accountId] === "undefined" ||
        typeof assignedStaffRecruiters[accountId] === "undefined",
    );
    if (missingIds.length === 0) return;

    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        missingIds.map(async (accountId) => {
          try {
            const resp = await fetch(
              `${BASE_URL}/api/personnel/accounts/${accountId}`,
              {
                credentials: "include",
                headers: { ...getAuthHeaders() },
              },
            );
            if (!resp.ok) return [accountId, "", ""];
            const json = await resp.json().catch(() => null);
            const data = json?.data ?? json ?? {};
            const phone =
              data?.phone_mobile ||
              data?.phone ||
              data?.mobile ||
              data?.phone_home ||
              "";
            const recruiterName = String(
              data?.recruiterName || data?.recruiter_name || "",
            ).trim();
            return [accountId, sanitizePhone(phone), recruiterName];
          } catch {
            return [accountId, "", ""];
          }
        }),
      );

      if (cancelled) return;
      setAssignedStaffPhones((prev) => {
        const next = { ...prev };
        for (const [accountId, phone] of results) {
          next[String(accountId)] = phone;
        }
        return next;
      });
      setAssignedStaffRecruiters((prev) => {
        const next = { ...prev };
        for (const [accountId, , recruiterName] of results) {
          next[String(accountId)] = recruiterName;
        }
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [realAssignments, assignedStaffPhones, assignedStaffRecruiters]);

  // Debounced search against personnel accounts search endpoint
  const doSearch = async (q, page = 1, activeTab = selectedStaffTab) => {
    const requestSeq = ++staffSearchSeqRef.current;
    setStaffLoading(true);
    try {
      const params = new URLSearchParams();
      const searchQuery = (q || "").trim();

      const slotShiftId =
        assignment?.sourceShiftId ?? assignment?.raw?.sourceShiftId ?? null;
      const shiftIdCandidate =
        slotShiftId ||
        entry?.shift_id ||
        entry?.id ||
        shift?.shift_id ||
        shift?.id ||
        null;
      let url;

      // Use worker-type endpoint based on active staff tab.
      params.set("page", String(page));
      params.set("limit", String(STAFF_PAGE_SIZE));
      params.set("search", searchQuery || "");

      // Always pass shift_id so backend can derive shift location if needed
      if (shiftIdCandidate) params.set("shift_id", String(shiftIdCandidate));

      const shiftObj = entry?.rawShift || entry || shift || {};
      const shiftForUtilization =
        resolveSlotRawShiftForPanel(entry, assignment, shift) || shiftObj;
      const utilizationYmd =
        utilizationAnchorYmdFromShiftObj(shiftForUtilization);
      if (utilizationYmd) params.set("utilization", utilizationYmd);

      const { positionId, positionLabel } = panelShiftPositionMeta;
      if (panelShiftClientId != null) {
        params.set("clientId", String(panelShiftClientId));
        params.set("client_id", String(panelShiftClientId));
      }
      if (positionId != null) params.set("position_id", String(positionId));
      if (positionLabel) params.set("position", positionLabel);

      if (appliedFilters?.sortBy)
        params.set("sortBy", appliedFilters.sortBy.toLowerCase());
      if (appliedFilters?.sortDirection)
        params.set("sortDirection", appliedFilters.sortDirection.toLowerCase());

      if (activeTab !== "office") {
        // Assign flow: allow assignable temp staff, including pending activation.
        params.set("assignableOnly", "1");
        // Pass ignoreAvailability so backend can filter out conflicting assignments / unmet requirements
        params.set(
          "ignoreAvailability",
          appliedFilters?.ignoreAvailability ? "1" : "0",
        );
        // Shift-linked skill IDs from Jobs/Schedule (shift_skills); omit when ignoring requirements.
        if (
          !appliedFilters?.ignoreAvailability &&
          Array.isArray(appliedFilters?.skills) &&
          appliedFilters.skills.length > 0
        ) {
          params.set("skillIds", appliedFilters.skills.join(","));
        }
      }

      url =
        activeTab === "office"
          ? `${BASE_URL}/api/personnel/office?${params.toString()}`
          : `${BASE_URL}/api/personnel/temp?${params.toString()}`;

      console.log(`Fetching ${activeTab} staff from:`, url);
      console.log("Applied filters:", appliedFilters);
      console.log("Entry data:", {
        shift_id: entry?.shift_id || entry?.id,
        clientId: panelShiftClientId,
        division_id: entry?.division_id || entry?.rawShift?.division_id,
        position_id: positionId,
        position: positionLabel,
        assignments: entry?.assignments?.length,
      });

      const resp = await fetch(url, { headers: { ...getAuthHeaders() } });

      if (!resp.ok) {
        console.error("API request failed:", resp.status, resp.statusText);
        if (requestSeq === staffSearchSeqRef.current) {
          setStaffData([]);
        }
        setStaffLoading(false);
        return;
      }

      const j = await resp.json().catch((err) => {
        console.error("Failed to parse JSON response:", err);
        return {};
      });

      console.log("API response:", j);

      // Handle both response formats: { data: [...] } or { success: true, data: [...] } or just [...]
      let list = [];
      if (j.data && Array.isArray(j.data)) {
        list = j.data;
      } else if (Array.isArray(j)) {
        list = j;
      }

      console.log("Extracted list:", list.length, "items");

      const mapped = Array.isArray(list)
        ? list.map((a, index) => {
            // Format proximity: show as "X.X km" if available, otherwise empty string
            const proximityValue =
              a.proximity !== undefined && a.proximity !== null
                ? parseFloat(a.proximity)
                : a.proximityKm !== undefined && a.proximityKm !== null
                  ? parseFloat(a.proximityKm)
                  : null;
            const proximityDisplay =
              proximityValue !== null && !isNaN(proximityValue)
                ? `${proximityValue.toFixed(1)} km`
                : "";

            // Debug first few items
            if (index < 3) {
              console.log("Staff proximity mapping:", {
                name:
                  a.name || `${a.first_name || ""} ${a.last_name || ""}`.trim(),
                rawProximity: a.proximity,
                rawProximityKm: a.proximityKm,
                proximityValue,
                proximityDisplay,
                staff_lat: a.staff_lat,
                staff_lng: a.staff_lng,
                address_id: a.staff_address_id || a.address_id,
              });
            }

            const utilFmt = formatTempStaffUtilizationFromApi(a);

            return {
              id: a.account_id || a.id,
              name:
                a.name || `${a.first_name || ""} ${a.last_name || ""}`.trim(),
              first_name: a.first_name || "",
              last_name: a.last_name || "",
              email: a.email || "",
              division: a.division || a.division_name || a.divisionName || "",
              payer: a.payer || a.agency_name || "",
              type: a.type || a.account_type || "Field Staff",
              phone: a.phone_mobile || a.phone || a.phone_home || "",
              staff_pay_rates: Array.isArray(a.staff_pay_rates)
                ? a.staff_pay_rates
                : [],
              proximity: proximityDisplay,
              proximityValue: proximityValue,
              reliability: a.reliability || "",
              utilization:
                utilFmt.utilization || a.utilization || "",
              hours: utilFmt.hours || a.hours || "",
            };
          })
        : [];

      console.log("📊 Processed staff data:", {
        total: mapped.length,
        withProximity: mapped.filter((s) => s.proximityValue !== null).length,
        withoutProximity: mapped.filter((s) => s.proximityValue === null)
          .length,
        sampleWithProximity: mapped.find((s) => s.proximityValue !== null),
        sampleWithoutProximity: mapped.find((s) => s.proximityValue === null),
      });

      // Store pagination metadata from backend
      const backendTotal =
        typeof j.total === "number" ? j.total : mapped.length;
      const backendTotalPages =
        typeof j.totalPages === "number"
          ? j.totalPages
          : Math.ceil(backendTotal / STAFF_PAGE_SIZE);
      let filtered = mapped;
      if (activeTab !== "office") {
        // The /temp endpoint already filters by temp staff types, so this is just a safety check.
        const tempStaffTypes = ["fieldstaff", "candidate", "outsourcedstaff"];
        filtered = mapped.filter((staff) => {
          const staffType = (staff.type || "").toLowerCase();
          return (
            !staffType ||
            tempStaffTypes.some((type) =>
              staffType.includes(type.toLowerCase()),
            )
          );
        });
      }

      if (searchQuery) {
        filtered = filtered.filter((staff) =>
          matchesPersonnelSearch(staff, searchQuery),
        );
        // /temp already returns best name matches first; keep that order.
        if (activeTab === "office") filtered.sort(sortPersonnelAlphabetically);
      }

      const hasClientPruned = Boolean(searchQuery) && filtered.length < mapped.length;
      const displayTotal = hasClientPruned ? filtered.length : backendTotal;
      setStaffTotalCount(displayTotal);
      setStaffTotalPages(Math.max(1, Math.ceil(displayTotal / STAFF_PAGE_SIZE)));

      console.log(
        `Loaded ${filtered.length} ${activeTab} staff members (from ${mapped.length} total, page ${page}/${backendTotalPages})`,
      );
      if (requestSeq !== staffSearchSeqRef.current) {
        return;
      }
      setStaffData(filtered);
    } catch (e) {
      console.error("Staff search failed", e);
      if (requestSeq === staffSearchSeqRef.current) {
        setStaffData([]);
      }
    } finally {
      if (requestSeq === staffSearchSeqRef.current) {
        setStaffLoading(false);
      }
    }
  };

  const refreshTempStaffTable = () => {
    void doSearch(searchTerm.trim(), staffPage);
  };

  useEffect(() => {
    const loadClient = async () => {
      if (!entry?.client_id) return;
      try {
        const resp = await fetch(`${BASE_URL}/api/personnel/clients`, {
          headers: { ...getAuthHeaders() },
        });
        const j = await resp.json().catch(() => ({}));
        const list = j.data || j || [];
        const found = Array.isArray(list)
          ? list.find(
              (c) =>
                c.client_id === entry.client_id || c.id === entry.client_id,
            )
          : null;
        if (found) setClientName(found.name || "");
      } catch (e) {
        console.error("Failed to load clients", e);
      }
    };
    loadClient();
    fetchOutsourcedPayers();
  }, [panelShiftId, entry?.client_id, hasRealAssignedStaff, isUnassignedSlotView]);

  useEffect(() => {
    if (selectedStaffTab === "outsourced") return undefined;
    setStaffPage(1);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(
      () => doSearch(searchTerm.trim(), 1, selectedStaffTab),
      250,
    );
    return () => clearTimeout(searchTimer.current);
  }, [
    searchTerm,
    appliedFilters,
    selectedStaffTab,
    panelShiftId,
    hasRealAssignedStaff,
    isUnassignedSlotView,
  ]);

  // Sync local note state when entry/shift changes (shift-level fields match ShiftDetailModal).
  useEffect(() => {
    const temp =
      shift?.note ??
      shift?.note_to_staff ??
      entry?.note ??
      entry?.note_to_staff ??
      null;
    const admin = shift?.admin_note ?? entry?.admin_note ?? null;
    setLocalNote(
      temp != null && String(temp).trim() ? String(temp).trim() : null,
    );
    setLocalAdminNote(
      admin != null && String(admin).trim() ? String(admin).trim() : null,
    );
  }, [entry, shift]);

  const getShiftId = () => {
    const sid = resolveSchedulePanelShiftId(shift, entry, assignment);
    if (sid == null || sid === "") {
      console.error(
        "ShiftDetailPanel: missing shift id (shift, entry, assignment):",
        { shift, entry, assignment },
      );
    }
    return sid;
  };

  const refreshParentSchedule = async () => {
    try {
      if (typeof onScheduleRefreshAwait === "function") {
        await onScheduleRefreshAwait();
        // Let Schedule commit scheduleData + run the open-panel sync effect before staff search reruns.
        await new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });
      } else if (typeof onScheduleRefresh === "function") {
        onScheduleRefresh();
      }
    } catch (e) {
      console.error("Schedule refresh failed", e);
    }
  };

  const patchShiftMetadata = async (body) => {
    const sid = getShiftId();
    if (!sid) {
      showError("Shift ID not found");
      return false;
    }
    const token = getAuthToken();
    const resp = await fetch(`${BASE_URL}/api/shifts/${sid}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok || json.success === false) {
      throw new Error(json.message || "Update failed");
    }
    return true;
  };

  const handleNotifyStaffToggle = async () => {
    if (readOnly || isClosedWeek) return;
    try {
      const next = !assignmentFlags.notifyStaffChange;
      await patchShiftMetadata({ notify_staff_change: next ? 1 : 0 });
      await refreshParentSchedule();
    } catch (e) {
      console.error(e);
      showError(e.message || "Failed to update notification flag");
    }
  };

  const handleCarpoolSave = async ({ carpool_name, carpool_note }) => {
    if (readOnly || isClosedWeek) return;
    setCarpoolSaving(true);
    try {
      await patchShiftMetadata({ carpool_name, carpool_note });
      showSuccess("Carpool details saved");
      setCarpoolModalOpen(false);
      await refreshParentSchedule();
    } catch (e) {
      console.error(e);
      showError(e.message || "Failed to save carpool");
    } finally {
      setCarpoolSaving(false);
    }
  };

  /** Opens UnassignShiftDialog then assigns `staffMember` on success (shared by full-shift guard + API 409). */
  const openReplaceAssignForNewStaff = async (staffMember) => {
    const guardRaw =
      resolveSlotRawShiftForPanel(entry, assignment, shift) ||
      entry?.rawShift ||
      entry?.raw ||
      shift ||
      {};
    const guardShiftId = resolveSchedulePanelShiftId(shift, entry, assignment);
    if (
      await shiftBlocksUnassignBySubmittedActuals(
        guardShiftId,
        guardRaw,
        getAuthHeaders(),
      )
    ) {
      showError(UNASSIGN_ACTUALS_SUBMITTED_MSG);
      return false;
    }
    const candidates = slotRealAssignments;
    if (!candidates.length) {
      showError(
        "This shift has no assignee to replace. Unassign or add capacity in Jobs, then try again.",
      );
      return false;
    }
    let target = candidates[0];
    if (assignment) {
      const ctxId = Number(
        assignment.employee?.account_id ||
          assignment.account_id ||
          assignment.employee?.id,
      );
      if (Number.isFinite(ctxId) && ctxId > 0) {
        const found = candidates.find((a) => {
          const id = Number(
            a.employee?.account_id ||
              a.account_id ||
              a.employee?.id,
          );
          return id === ctxId;
        });
        if (found) target = found;
      }
    }
    const occupantEmp = target.employee || target.emp || {};
    const occupantAccountId = Number(
      occupantEmp.account_id || target.account_id || occupantEmp.id,
    );
    if (!Number.isFinite(occupantAccountId) || occupantAccountId <= 0) {
      showError(
        "Could not determine who to unassign. Use Actions → Unassign.",
      );
      return false;
    }
    const staffDisplayName =
      occupantEmp.name ||
      `${occupantEmp.first_name || ""} ${occupantEmp.last_name || ""}`.trim() ||
      "Staff";
    const raw = entry?.rawShift || entry?.raw || shift || {};
    pendingAssignAfterUnassignRef.current = staffMember;
    setScheduleUnassignPayload({
      shiftId: resolveSchedulePanelShiftId(shift, entry, assignment),
      accountId: occupantAccountId,
      staffDisplayName,
      startAt:
        raw.start_date ||
        raw.scheduledStart ||
        raw.startDate ||
        entry?.scheduledStart ||
        null,
      endAt:
        raw.end_date ||
        raw.scheduledEnd ||
        raw.endDate ||
        entry?.scheduledEnd ||
        null,
      silentUnassignSuccess: true,
    });
    setScheduleUnassignOpen(true);
    return true;
  };

  /** Jobs shift modal must use the opened slot's id (merged Schedule rows used to pass only the first shift). */
  const buildShiftPayloadForJobsModal = () => {
    const sid = getShiftId();
    const base = shift || entry?.rawShift || entry || {};
    if (sid === undefined || sid === null || sid === "") return base;
    const n = Number(sid);
    const idVal = Number.isFinite(n) ? n : sid;
    return {
      ...base,
      id: idVal,
      shift_id: idVal,
      shiftId: idVal,
    };
  };

  const fetchShiftNotes = async () => {
    const sid = getShiftId();
    if (!sid) return null;

    // Shift-level notes (shifts.note / shifts.admin_note) — same fields as ShiftDetailModal.
    try {
      const resp = await fetch(`${BASE_URL}/api/jobs/shifts/${sid}`, {
        credentials: "include",
        headers: { ...getAuthHeaders() },
      });
      const j = await resp.json().catch(() => null);
      const data = j?.data || j || {};
      const tempNote = data.note ?? data.note_to_staff ?? null;
      const adminNote = data.admin_note ?? null;
      const trimmedTemp =
        tempNote != null && String(tempNote).trim()
          ? String(tempNote).trim()
          : null;
      const trimmedAdmin =
        adminNote != null && String(adminNote).trim()
          ? String(adminNote).trim()
          : null;
      setLocalNote(trimmedTemp);
      setLocalAdminNote(trimmedAdmin);
      return {
        note: trimmedTemp,
        note_to_staff: trimmedTemp,
        admin_note: trimmedAdmin,
      };
    } catch (e) {
      console.error("Failed to fetch shift notes", e);
      return null;
    }
  };

  const saveNoteToBackend = async (type) => {
    const sid = getShiftId();
    if (!sid) return { ok: false, message: "Missing shift id" };

    try {
      const payload = {};
      if (type === "staff") payload.note_to_staff = noteDraft;
      else payload.admin_note = adminDraft;
      const resp = await fetch(`${BASE_URL}/api/jobs/shifts/${sid}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        let msg = "";
        try {
          msg = await resp.text();
        } catch (_) {
          msg = `${resp.status} ${resp.statusText}`;
        }
        console.error("Save note failed", resp.status, msg);
        return { ok: false, message: msg || "Save failed" };
      }
      return { ok: true };
    } catch (e) {
      console.error("Failed to save note", e);
      return { ok: false, message: e.message || "Network error" };
    }
  };

  // Refresh notes from API when the panel opens or the shift identity changes.
  useEffect(() => {
    const sid = getShiftId();
    if (!sid) return;
    void fetchShiftNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when shift identity changes
  }, [
    shift?.shift_id,
    shift?.id,
    entry?.shift_id,
    entry?.id,
    assignment?.shift_id,
  ]);

  // Load reliability (TempReliability source) + utilization metrics for panel accounts
  useEffect(() => {
    const assignmentIds = (visibleAssignments || [])
      .map((a) => a.employee?.account_id || a.account_id || a.employee?.id)
      .filter(Boolean);
    const staffIds = (staffData || [])
      .map((s) => s.id ?? s.account_id ?? s.accountId)
      .filter(Boolean);
    const ids = [
      ...new Set([...assignmentIds, ...staffIds].map((id) => String(id))),
    ];
    if (!ids.length) return;

    let cancelled = false;
    const shiftForUtilization =
      resolveSlotRawShiftForPanel(entry, assignment, shift) || entry || shift || {};
    const utilizationAnchor =
      utilizationAnchorYmdFromShiftObj(shiftForUtilization);

    const loadMetrics = async () => {
      try {
        const headers = getAuthHeaders();
        const fetches = ids.map(async (id) => {
          const data = await fetchAccountMetricsBundle(
            id,
            headers,
            30,
            utilizationAnchor,
          );
          return { id, data };
        });
        const results = await Promise.all(fetches);
        if (cancelled) return;
        const map = {};
        for (const r of results) {
          if (r?.id && r.data) map[String(r.id)] = r.data;
        }
        setMetricsMap(map);
      } catch (e) {
        console.error("Failed to load metrics for assignments", e);
      }
    };

    loadMetrics();
    return () => {
      cancelled = true;
    };
  }, [visibleAssignments, staffData]);

  const getReliabilityPercent = (accountId, fallbackSources = []) => {
    const key =
      accountId !== undefined && accountId !== null && accountId !== ""
        ? String(accountId)
        : "";
    if (key && metricsMap[key]?.reliability != null) {
      const n = Number(metricsMap[key].reliability);
      if (Number.isFinite(n)) return Math.round(n);
    }
    for (const src of fallbackSources) {
      const n = parseReliabilityPercent(src);
      if (n) return Math.round(n);
    }
    return 0;
  };

  const handleApplyFilters = (filters, mode = "temp") => {
    if (mode === "outsourced") {
      // Only use sort fields for outsourced mode
      setOutsourcedSortBy(filters.sortBy || "Outsourced Agency");
      setOutsourcedSortDirection(filters.sortDirection || "Ascending");
      return;
    }
    // Merge new filters with existing division_ids and skills (preserve auto-applied values)
    setAppliedFilters((prev) => ({
      ...filters,
      division_ids: prev.division_ids || filters.division_ids || [],
      skills: prev.skills || filters.skills || [],
    }));
  };

  // Filter and sort staff based on applied filters
  const getFilteredAndSortedStaff = () => {
    const source = staffData && staffData.length > 0 ? staffData : [];
    let filtered = source.filter((staff) => {
      const staffId =
        staff?.id ?? staff?.account_id ?? staff?.accountId ?? null;
      if (
        staffId !== null &&
        staffId !== undefined &&
        staffId !== "" &&
        mergedBlockAssignedAccountKeys.has(String(staffId)) &&
        !isUnassignedSlotView
      ) {
        return false;
      }

      if (
        searchTerm.trim() &&
        !matchesPersonnelSearch(staff, searchTerm.trim())
      ) {
        return false;
      }

      // Type filter (case-insensitive: "Field Staff" matches "fieldstaff", "Candidate" matches "candidate")
      if (appliedFilters.types.length > 0) {
        const norm = (t) => (t || "").toLowerCase().replace(/[\s_-]/g, "");
        const selectedNorm = appliedFilters.types.map(norm);
        const staffNorm = norm(staff.type);
        if (
          !selectedNorm.some(
            (nt) => staffNorm.includes(nt) || nt.includes(staffNorm),
          )
        ) {
          return false;
        }
      }

      // Proximity filters
      if (appliedFilters.minProximity) {
        const minValue = parseFloat(
          appliedFilters.minProximity.replace(/[^\d.]/g, ""),
        );
        if (!isNaN(minValue) && staff.proximityValue < minValue) {
          return false;
        }
      }

      if (appliedFilters.maxProximity) {
        const maxValue = parseFloat(
          appliedFilters.maxProximity.replace(/[^\d.]/g, ""),
        );
        if (!isNaN(maxValue) && staff.proximityValue > maxValue) {
          return false;
        }
      }

      return true;
    });

    // Sort staff
    if (searchTerm.trim().length > 0) {
      // /temp already returns best name matches first; keep that order.
      if (selectedStaffTab === "office") {
        filtered.sort((a, b) =>
          appliedFilters.sortDirection === "Descending"
            ? sortPersonnelAlphabetically(b, a)
            : sortPersonnelAlphabetically(a, b),
        );
      }
      return filtered;
    }

    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (appliedFilters.sortBy) {
        case "Reliability":
          aValue = getReliabilityPercent(a.id ?? a.account_id, [a.reliability]);
          bValue = getReliabilityPercent(b.id ?? b.account_id, [b.reliability]);
          break;
        case "Utilization":
          aValue =
            parseFloat((a.utilization || "").replace(/[^0-9.]/g, "")) || 0;
          bValue =
            parseFloat((b.utilization || "").replace(/[^0-9.]/g, "")) || 0;
          break;
        case "Proximity":
          aValue = Number.isFinite(a.proximityValue) ? a.proximityValue : Number.POSITIVE_INFINITY;
          bValue = Number.isFinite(b.proximityValue) ? b.proximityValue : Number.POSITIVE_INFINITY;
          break;
        case "Staff First Name":
          aValue = (a.name || "").split(" ")[0];
          bValue = (b.name || "").split(" ")[0];
          break;
        case "Staff Last Name":
          aValue = (a.name || "").split(" ").slice(1).join(" ") || "";
          bValue = (b.name || "").split(" ").slice(1).join(" ") || "";
          break;
        case "Preference":
        default:
          aValue = getReliabilityPercent(a.id ?? a.account_id, [a.reliability]);
          bValue = getReliabilityPercent(b.id ?? b.account_id, [b.reliability]);
      }

      let cmp = 0;
      if (typeof aValue === "string") {
        cmp = appliedFilters.sortDirection === "Ascending"
          ? aValue.localeCompare(bValue, undefined, { sensitivity: "base" })
          : bValue.localeCompare(aValue, undefined, { sensitivity: "base" });
      } else {
        cmp = appliedFilters.sortDirection === "Ascending"
          ? aValue - bValue
          : bValue - aValue;
      }

      if (cmp !== 0) return cmp;

      // Tie-breaker: alphabetical order by name
      return sortPersonnelAlphabetically(a, b);
    });

    return filtered;
  };

  const filteredStaff = getFilteredAndSortedStaff();

  const handleOpenPersonnelModal = (staffMember) => {
    // Determine if this is temp staff or office staff based on their type.
    const rawType = (staffMember.type || staffMember.staffType || "")
      .toString()
      .toLowerCase();
    const isTemp =
      rawType.includes("field") ||
      rawType.includes("temp") ||
      rawType.includes("outsourced") ||
      rawType.includes("candidate");

    const accountId =
      staffMember.account_id || staffMember.id || staffMember.accountId || null;
    const phoneRaw = staffMember.phone || staffMember.phone_mobile || "";

    // If we have an account id, attempt to prefetch the full account record so
    // the Personnel modal shows the complete view (same as Personnel tab).
    if (accountId) {
      (async () => {
        try {
          const resp = await fetch(
            `${BASE_URL}/api/personnel/accounts/${accountId}`,
            { headers: { ...getAuthHeaders() } },
          );
          if (resp.ok) {
            const json = await resp.json().catch(() => null);
            const data = json && json.data ? json.data : null;
            if (data) {
              const full = {
                id: data.account_id || data.accountId || accountId,
                ...data,
              };
              // Ensure `name` exists for modal header consistency
              if (!full.name)
                full.name = [full.first_name, full.last_name]
                  .filter(Boolean)
                  .join(" ")
                  .trim();
              if (onOpenPersonnelModal) onOpenPersonnelModal(full);
              return;
            }
          }
        } catch (e) {
          console.warn(
            "Prefetch account failed in ShiftDetailPanel, falling back",
            e,
          );
        }

        // Fallback to lightweight object if prefetch fails
        const fallback = {
          id: accountId || undefined,
          account_id: accountId,
          name: staffMember.name || "",
          phone: sanitizePhone(phoneRaw),
          email:
            staffMember.email ||
            (staffMember.name
              ? staffMember.name.toLowerCase().replace(/\s+/g, ".") +
                "@example.com"
              : ""),
          staffType: isTemp ? "temp" : "office",
          originalType: staffMember.type || staffMember.staffType,
          avatarText:
            staffMember.initials ||
            (staffMember.name || "")
              .split(" ")
              .map((n) => n[0])
              .join(""),
        };
        if (onOpenPersonnelModal) onOpenPersonnelModal(fallback);
      })();
      return;
    }

    // No account id — open modal with lightweight object
    const personObj = {
      id: undefined,
      account_id: undefined,
      name: staffMember.name || "",
      phone: sanitizePhone(phoneRaw),
      email:
        staffMember.email ||
        (staffMember.name
          ? staffMember.name.toLowerCase().replace(/\s+/g, ".") + "@example.com"
          : ""),
      staffType: isTemp ? "temp" : "office",
      originalType: staffMember.type || staffMember.staffType,
      avatarText:
        staffMember.initials ||
        (staffMember.name || "")
          .split(" ")
          .map((n) => n[0])
          .join(""),
    };
    if (onOpenPersonnelModal) onOpenPersonnelModal(personObj);
  };

  const performAssignRequest = async (staffMember) => {
    const shiftId = getShiftId();
    const accountId =
      staffMember.account_id || staffMember.id || staffMember.accountId;

    if (!shiftId) {
      showError("Shift ID not found");
      return;
    }
    if (!accountId) {
      showError("Staff account ID not found");
      return;
    }

    const token = getAuthToken();
    const response = await fetch(`${BASE_URL}/api/shifts/${shiftId}/assign`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        accountId,
        ignoreAvailability: appliedFilters.ignoreAvailability ? 1 : 0,
      }),
    });

    let result = {};
    try {
      result = await response.json();
    } catch {
      result = {};
    }

    const atCapacity =
      response.status === 409 &&
      (result?.code === "SHIFT_AT_CAPACITY" ||
        /all slots for this shift are filled/i.test(
          String(result?.message || ""),
        ));

    if (atCapacity) {
      if (await openReplaceAssignForNewStaff(staffMember)) return;
      throw new Error(
        result?.message ||
          "All slots for this shift are filled. Unassign someone before assigning another person.",
      );
    }

    if (!response.ok) {
      throw new Error(result.message || "Failed to assign staff");
    }

    showToast(`Successfully assigned ${staffMember.name} to the shift`);

    if (onOpenShiftModal && !suppressShiftModalAfterAssign) {
      try {
        onOpenShiftModal(
          { ...buildShiftPayloadForJobsModal(), state: "assigned" },
          entry,
        );
      } catch (e) {
        /* ignore */
      }
    }

    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      doSearch(searchTerm.trim(), 1);
    }, 100);
    await refreshParentSchedule();
    setTimeout(() => {
      void doSearch(searchTerm.trim(), 1);
    }, 450);
  };

  // Handle assigning staff to the shift (enforces slot capacity; replace flow uses Unassign dialog)
  const handleAssignStaff = async (staffMember) => {
    if (isClosedWeek) {
      showError("This week is closed; assignments cannot be changed.");
      return;
    }
    const newAccountId = Number(
      staffMember.account_id ||
        staffMember.id ||
        staffMember.accountId,
    );
    if (!Number.isFinite(newAccountId) || newAccountId <= 0) {
      showError("Staff account ID not found");
      return;
    }

    const requiredSlots = requiredSlotsForActiveShift;
    const filled = slotRealAssignments.length;
    const alreadyOnShift = slotRealAssignments.some((a) => {
      const id = Number(
        a.employee?.account_id ||
          a.account_id ||
          a.employee?.id ||
          a.employee_id,
      );
      return Number.isFinite(id) && id === newAccountId;
    });
    if (alreadyOnShift) {
      showError("That person is already assigned to this shift.");
      return;
    }

    if (filled >= requiredSlots && slotRealAssignments.length > 0) {
      await openReplaceAssignForNewStaff(staffMember);
      return;
    }

    try {
      await performAssignRequest(staffMember);
    } catch (error) {
      console.error("Error assigning staff:", error);
      showError(error.message || "Failed to assign staff to shift");
    }
  };

  const renderAssignmentHeading = () => {
    const notifyEditable = !readOnly && !isClosedWeek;
    const carTitleParts = [];
    if (assignmentFlags.carpoolName)
      carTitleParts.push(assignmentFlags.carpoolName);
    if (assignmentFlags.carpoolName && assignmentFlags.carpoolNote)
      carTitleParts.push("•");
    if (assignmentFlags.carpoolNote)
      carTitleParts.push(assignmentFlags.carpoolNote);
    const carTitle = carTitleParts.join(" ").trim() || "Carpool";
    const hasCarpoolText =
      !!(assignmentFlags.carpoolName || assignmentFlags.carpoolNote);

    return (
      <div className="flex min-w-0 w-full flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-medium text-gray-900">Assignment</h3>
          {assignmentFlags.ignoreAvailability ? (
            <span
              title="Availability & requirements being ignored"
              className="inline-flex shrink-0"
              role="img"
              aria-label="Availability and requirements being ignored"
            >
              <MdDoNotDisturbOff className="text-gray-500" size={15} />
            </span>
          ) : null}
          {notifyEditable ? (
            <button
              type="button"
              title="Staff requires notification of change."
              onClick={handleNotifyStaffToggle}
              className="inline-flex shrink-0 border-0 bg-transparent p-0 cursor-pointer"
              aria-pressed={assignmentFlags.notifyStaffChange}
            >
              <FaFlag
                className={
                  assignmentFlags.notifyStaffChange
                    ? "text-red-600"
                    : "text-gray-500"
                }
                size={14}
              />
            </button>
          ) : (
            <span
              title="Staff requires notification of change."
              className="inline-flex shrink-0"
              role="img"
              aria-label="Staff requires notification of change"
            >
              <FaFlag
                className={
                  assignmentFlags.notifyStaffChange
                    ? "text-red-600"
                    : "text-gray-500"
                }
                size={14}
              />
            </span>
          )}
          <button
            type="button"
            title={carTitle}
            onClick={() => setCarpoolModalOpen(true)}
            className="inline-flex shrink-0 border-0 bg-transparent p-0 cursor-pointer"
          >
            <FaCar
              className={
                hasCarpoolText ? "text-red-600" : "text-gray-500"
              }
              size={14}
            />
          </button>
        </div>
        {hasCarpoolText ? (
          <button
            type="button"
            title={carTitle}
            onClick={() => setCarpoolModalOpen(true)}
            className="w-full min-w-0 border-0 bg-transparent p-0 cursor-pointer text-left"
          >
            <span className="block min-w-0 text-xs text-red-600 [overflow-wrap:anywhere] break-all whitespace-normal">
              {assignmentFlags.carpoolName && assignmentFlags.carpoolNote
                ? `${assignmentFlags.carpoolName} • ${assignmentFlags.carpoolNote}`
                : assignmentFlags.carpoolName || assignmentFlags.carpoolNote}
            </span>
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <div>
      {/* Overview and Schedule Section - Side by Side, with Edit button */}
      <div className="mb-6 flex justify-between items-start gap-4">
        {/* Overview Section */}
        <div className="flex-1">
          <p className="text-xs text-gray-500 mb-1">Overview</p>
          <p className="font-medium text-gray-900 inline-flex items-center gap-1.5">
            {panelPositionLabel || "—"}
            {panelRequiredGender === "male" ? (
              <FaMale className="text-blue-600" size={18} title="Male required" />
            ) : panelRequiredGender === "female" ? (
              <FaFemale className="text-pink-500" size={18} title="Female required" />
            ) : null}
          </p>
          <p className="text-sm text-gray-600">
            {(() => {
              const shiftObj = entry?.rawShift || entry || shift || {};
              const locationName = shiftObj.locationName || entry?.sublocation || "";
              const locationAddress = shiftObj.locationAddress || "";
              const fullLocation =
                [locationName, locationAddress].filter(Boolean).join(", ") ||
                "Primary Location, 1 Commerce Valley Drive E., Thornhill, ON, L3T 7X6, CA";
              return clientName ? `${clientName} · ${fullLocation}` : fullLocation;
            })()}
          </p>
        </div>

        {/* Schedule Section */}
        <div className="flex-1 text-right">
          <p className="text-xs text-gray-500 mb-1">Schedule</p>
          <p className="text-sm text-gray-900">
            {context?.time || entry?.time || "Dec 1 · 5:30 pm – Dec 2, 1:30 am"}
          </p>
          <p className="text-xs text-gray-500">
            {panelScheduleDurationText || "—"}
          </p>
        </div>

        {/* Edit button to open full Jobs shift popup (always enabled, even for closed weeks) */}
        {typeof onOpenShiftModal === "function" && (
          <div className="ml-2">
            <button
              type="button"
              className="p-2 border border-gray-300 rounded-full hover:bg-gray-50 flex items-center justify-center"
              aria-label="Edit shift"
              onClick={() => {
                onOpenShiftModal(buildShiftPayloadForJobsModal(), entry);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-4 h-4 text-gray-600"
              >
                <path d="M13.586 3.586a2 2 0 0 1 2.828 2.828l-8.086 8.086A2 2 0 0 1 7.293 15H4a1 1 0 0 1-1-1v-3.293a2 2 0 0 1 .586-1.414l8-8Z" />
                <path d="M5 13h2.586L14 6.586 12.414 5 5 12.414V13Z" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Pay Rate */}
      <div className="mb-6">
        <p className="text-xs text-gray-500 mb-1">Pay Rate</p>
        <p className="text-sm">
          <span className="font-medium text-gray-900">
            {(() => {
              const payRate =
                entry?.pay_rate ??
                entry?.rawShift?.pay_rate ??
                shift?.pay_rate ??
                shift?.payRate;
              const num = payRate != null ? Number(payRate) : NaN;
              return Number.isFinite(num)
                ? `$${num.toFixed(2)} /h`
                : "—";
            })()}
          </span>
          <span
            className="text-red-500 ml-2 cursor-pointer hover:text-red-600 hover:underline"
            onClick={() => {
              if (onOpenShiftModal) {
                onOpenShiftModal(buildShiftPayloadForJobsModal(), entry);
              }
            }}
          >
            {sidebarHasTravel
              ? travelPayRateSuffix(
                  sidebarTravelTypeRaw,
                  sidebarTravelAmountRaw,
                )
              : "No travel time"}
          </span>
        </p>
      </div>

      {isClosedWeek && (
        <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          This week is closed. Assignment and edit actions are not available.
        </div>
      )}

      {/* Note to Temp Staff */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <p className="text-xs text-gray-500 mb-1">Note to Temp Staff</p>
          <p
            className={
              (localNote ?? shift?.note ?? entry?.note)
                ? "text-sm text-gray-900"
                : "text-sm text-gray-500 italic"
            }
          >
            {(localNote ?? shift?.note ?? entry?.note) ||
              "Currently there are no notes"}
          </p>
        </div>
        <button
          disabled={!!isClosedWeek}
          onClick={async () => {
            if (isClosedWeek) return;
            const data = await fetchShiftNotes();
            setNoteDraft(
              (data && (data.note || data.note_to_staff)) ??
                localNote ??
                entry?.note ??
                "",
            );
            setShowEditNoteModal(true);
          }}
          className={`px-4 py-1.5 border rounded text-sm ${isClosedWeek ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed" : "border-gray-300 hover:bg-gray-50"}`}
        >
          EDIT
        </button>
      </div>

      {/* Admin Note */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <p className="text-xs text-gray-500 mb-1">Admin Note</p>
          <p
            className={
              (localAdminNote ?? shift?.admin_note ?? entry?.admin_note)
                ? "text-sm text-gray-900"
                : "text-sm text-gray-500 italic"
            }
          >
            {(localAdminNote ?? shift?.admin_note ?? entry?.admin_note) ||
              "Currently there are no notes"}
          </p>
        </div>
        <button
          disabled={!!isClosedWeek}
          onClick={async () => {
            if (isClosedWeek) return;
            const data = await fetchShiftNotes();
            setAdminDraft(
              (data && data.admin_note) ??
                localAdminNote ??
                entry?.admin_note ??
                "",
            );
            setShowEditAdminModal(true);
          }}
          className={`px-4 py-1.5 border rounded text-sm ${isClosedWeek ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed" : "border-gray-300 hover:bg-gray-50"}`}
        >
          EDIT
        </button>
      </div>

      {/* Assignment Section - Only show if there are REAL assignments AND we're not viewing an unassigned slot */}
      {/* When clicking on an unassigned slot, entry._isUnassignedSlotView will be true */}
      {realAssignments &&
        realAssignments.length > 0 &&
        !entry?._isUnassignedSlotView && (
          <div className="border-t pt-4 mb-6">
            <div className="flex justify-between items-center mb-3">
              {renderAssignmentHeading()}
              <div className="relative">
                <button
                  className={`px-4 py-2 rounded flex items-center gap-2 text-sm ${
                    isActionsDisabled
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : "bg-gray-700 text-white hover:bg-gray-600"
                  }`}
                  disabled={isActionsDisabled}
                  onClick={() => {
                    if (isActionsDisabled) return;
                    setShowActionDropdown(!showActionDropdown);
                  }}
                >
                  ACTIONS
                  <span
                    className={`transform transition-transform ${
                      showActionDropdown ? "rotate-180" : ""
                    }`}
                  >
                    ▼
                  </span>
                </button>

                {/* Action Dropdown */}
                {showActionDropdown && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-[200px]">
                    {/* Change Assignment Status - Not clickable, just a header */}
                    <div className="px-4 py-2 text-sm font-medium text-gray-400 border-b border-gray-100">
                      Change Assignment Status
                    </div>

                    {/* Sub-options with left spacing */}
                    <button
                      className="w-full text-left px-8 py-2 text-sm text-black hover:bg-gray-50 hover:text-gray-900"
                      onClick={async () => {
                        try {
                          const sid = getShiftId();
                          const accountId =
                            contextEmp?.account_id ||
                            contextEmp?.id ||
                            assignment?.account_id;
                          if (!sid || !accountId) {
                            showError(
                              "Missing shift or staff to set as tentative",
                            );
                            setShowActionDropdown(false);
                            return;
                          }
                          const token = getAuthToken();
                          const resp = await fetch(
                            `${BASE_URL}/api/shifts/${sid}/confirm-assignment`,
                            {
                              method: "POST",
                              headers: {
                                "Content-Type": "application/json",
                                ...(token
                                  ? { Authorization: `Bearer ${token}` }
                                  : {}),
                              },
                              body: JSON.stringify({
                                accountId,
                                state: "pending",
                              }),
                            },
                          );
                          const json = await resp.json().catch(() => ({}));
                          if (!resp.ok || !json.success) {
                            throw new Error(
                              json.message ||
                                "Failed to set assignment as tentative",
                            );
                          }
                          showToast("Assignment set as tentative");
                          setShowActionDropdown(false);
                          // refresh staff list so UI reflects new status
                          if (searchTimer.current)
                            clearTimeout(searchTimer.current);
                          searchTimer.current = setTimeout(
                            () => doSearch(searchTerm.trim(), staffPage),
                            200,
                          );
                          await refreshParentSchedule();
                          setTimeout(() => refreshTempStaffTable(), 400);
                        } catch (e) {
                          console.error("Set as Tentative failed", e);
                          showError(
                            e.message ||
                              "Failed to set assignment as tentative",
                          );
                          setShowActionDropdown(false);
                        }
                      }}
                    >
                      Set as Tentative
                    </button>

                    <button
                      className="w-full text-left px-8 py-2 text-sm text-black hover:bg-gray-50 hover:text-gray-900"
                      onClick={async () => {
                        try {
                          const sid = getShiftId();
                          const accountId =
                            contextEmp?.account_id ||
                            contextEmp?.id ||
                            assignment?.account_id;
                          if (!sid || !accountId) {
                            showError("Missing shift or staff to confirm");
                            setShowActionDropdown(false);
                            return;
                          }
                          const token = getAuthToken();
                          const resp = await fetch(
                            `${BASE_URL}/api/shifts/${sid}/confirm-assignment`,
                            {
                              method: "POST",
                              headers: {
                                "Content-Type": "application/json",
                                ...(token
                                  ? { Authorization: `Bearer ${token}` }
                                  : {}),
                              },
                              body: JSON.stringify({ accountId }),
                            },
                          );
                          const json = await resp.json().catch(() => ({}));
                          if (!resp.ok || !json.success) {
                            throw new Error(
                              json.message || "Failed to confirm assignment",
                            );
                          }
                          showToast("Assignment confirmed");
                          setShowActionDropdown(false);
                          // refresh staff list so UI reflects new status
                          if (searchTimer.current)
                            clearTimeout(searchTimer.current);
                          searchTimer.current = setTimeout(
                            () => doSearch(searchTerm.trim(), staffPage),
                            200,
                          );
                          await refreshParentSchedule();
                          setTimeout(() => refreshTempStaffTable(), 400);
                        } catch (e) {
                          console.error("Confirm assignment failed", e);
                          showError(
                            e.message || "Failed to confirm assignment",
                          );
                          setShowActionDropdown(false);
                        }
                      }}
                    >
                      Confirm
                    </button>

                    <button
                      className="w-full text-left px-8 py-2 text-sm text-black hover:bg-gray-50 hover:text-gray-900"
                      onClick={async () => {
                        const sid = getShiftId();
                        const accountId =
                          contextEmp?.account_id ||
                          contextEmp?.id ||
                          assignment?.account_id;
                        if (!sid || !accountId) {
                          showError("Missing shift or staff to unassign");
                          setShowActionDropdown(false);
                          return;
                        }
                        const raw =
                          resolveSlotRawShiftForPanel(entry, assignment, shift) ||
                          entry?.rawShift ||
                          entry?.raw ||
                          shift ||
                          {};
                        if (
                          await shiftBlocksUnassignBySubmittedActuals(
                            sid,
                            raw,
                            getAuthHeaders(),
                          )
                        ) {
                          showError(UNASSIGN_ACTUALS_SUBMITTED_MSG);
                          setShowActionDropdown(false);
                          return;
                        }
                        const startAt =
                          raw.start_date ||
                          raw.scheduledStart ||
                          raw.startDate ||
                          entry?.scheduledStart ||
                          null;
                        const endAt =
                          raw.end_date ||
                          raw.scheduledEnd ||
                          raw.endDate ||
                          entry?.scheduledEnd ||
                          null;
                        const staffDisplayName =
                          contextEmp?.name ||
                          `${contextEmp?.first_name || ""} ${contextEmp?.last_name || ""}`.trim() ||
                          "Staff";
                        setScheduleUnassignPayload({
                          shiftId: sid,
                          accountId,
                          staffDisplayName,
                          startAt,
                          endAt,
                          silentUnassignSuccess: false,
                        });
                        setScheduleUnassignOpen(true);
                        setShowActionDropdown(false);
                      }}
                    >
                      Unassign
                    </button>

                    {/* Assign to Similar — temp staff only (legacy parity) */}
                    {staffAccountId ? (
                      <div className="border-t border-gray-100">
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-black hover:bg-gray-50 hover:text-gray-900"
                          onClick={async () => {
                            setShowActionDropdown(false);
                            const sid = getShiftId();
                            const base =
                              resolveSlotRawShiftForPanel(
                                entry,
                                assignment,
                                shift,
                              ) ||
                              entry?.rawShift ||
                              entry ||
                              shift;
                            let full = null;
                            if (sid) {
                              try {
                                const resp = await fetch(
                                  `${BASE_URL}/api/jobs/shifts/${sid}`,
                                  { headers: { ...getAuthHeaders() } },
                                );
                                if (resp.ok) {
                                  const j = await resp.json().catch(() => null);
                                  full = (j && (j.data || j)) || null;
                                }
                              } catch {
                                /* use base shift context */
                              }
                            }
                            setModalOriginalShift({ ...base, ...(full || {}) });
                            setShowAssignSimilarModal(true);
                          }}
                        >
                          Assign to Similar
                        </button>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
            {realAssignments.map((assignment, idx) => (
              <div key={idx} className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-6 text-xs mb-4">
                <div className="w-full sm:w-52 sm:shrink-0">
                  <p className="text-gray-500">
                    ID:{" "}
                    {assignment.employee?.account_id ||
                      assignment.employee?.id ||
                      assignment.account_id ||
                      assignment.id ||
                      ""}
                  </p>
                  <div className="flex items-center gap-2 mt-1 min-w-0">
                    <StaffAvatarCircle
                      employee={assignment.employee || null}
                      accountId={
                        assignment.employee?.account_id ||
                        assignment.account_id ||
                        assignment.employee?.id
                      }
                      className="w-8 h-8 shrink-0"
                      textClassName="text-xs font-medium text-gray-700"
                      emptyLabel="?"
                    />
                    <div className="min-w-0 flex-1">
                      <p
                        className={`font-medium ${
                          assignment.employee
                            ? "text-red-500 cursor-pointer hover:text-red-600 hover:underline"
                            : "text-gray-700"
                        }`}
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                        onClick={() => {
                          if (!assignment.employee) return;
                          const displayName =
                            assignment.employee?.name ||
                            `${assignment.employee?.first_name || ""} ${assignment.employee?.last_name || ""}`.trim();
                          const resolvedId =
                            assignment.employee?.account_id ||
                            assignment.account_id ||
                            assignment.employee?.id ||
                            staffData.find((s) => s.name === displayName)?.id;
                          handleOpenPersonnelModal({
                            id: resolvedId,
                            account_id: resolvedId,
                            name: displayName,
                            initials: assignment.employee?.initials,
                            type: assignment.employee?.type || "Field Staff",
                          });
                        }}
                      >
                        {assignment.employee?.name ||
                          `${assignment.employee?.first_name || ""} ${assignment.employee?.last_name || ""}`.trim()}
                      </p>
                      <p className="text-gray-500">
                        {assignedStaffPhones[
                          String(
                            assignment.employee?.account_id ||
                              assignment.account_id ||
                              assignment.employee?.id ||
                              assignment.id ||
                              "",
                          )
                        ] ||
                          sanitizePhone(
                            assignment.employee?.phone ||
                              assignment.employee?.phone_mobile ||
                              assignment.employee?.phone_home,
                          ) ||
                          ""}
                      </p>
                      {(() => {
                        const accountKey = String(
                          assignment.employee?.account_id ||
                            assignment.account_id ||
                            assignment.employee?.id ||
                            assignment.id ||
                            "",
                        );
                        const recruiterName = String(
                          assignedStaffRecruiters[accountKey] ||
                            assignment.employee?.recruiterName ||
                            assignment.employee?.recruiter_name ||
                            "",
                        ).trim();
                        if (!recruiterName) return null;
                        return (
                          <p className="text-gray-500">
                            Recruiter: {recruiterName}
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 flex-1 min-w-0 w-full sm:w-auto">
                <div>
                  <p className="text-gray-500">Reliability</p>
                  <p className="font-medium">
                    {(() => {
                      const id =
                        assignment.employee?.account_id ||
                        assignment.account_id ||
                        assignment.employee?.id;
                      return `${getReliabilityPercent(id, [
                        assignment.reliability,
                        assignment.employee?.reliability,
                      ])}%`;
                    })()}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Utilization</p>
                  <p className="font-medium text-gray-900 tabular-nums">
                    {(() => {
                      const id =
                        assignment.employee?.account_id ||
                        assignment.account_id ||
                        assignment.employee?.id;
                      const val =
                        metricsMap[String(id)]?.utilization ??
                        assignment.utilization ??
                        assignment.employee?.utilization ??
                        0;
                      return `${Math.round(Number(val) || 0)}%`;
                    })()}
                  </p>
                  <p className="text-xs text-gray-400 tabular-nums">
                    {(() => {
                      const id =
                        assignment.employee?.account_id ||
                        assignment.account_id ||
                        assignment.employee?.id;
                      const m = metricsMap[String(id)];
                      const hw =
                        typeof m?.hours_worked === "number"
                          ? m.hours_worked
                          : null;
                      const maxH =
                        m?.max_hours != null && m.max_hours !== ""
                          ? Number(m.max_hours)
                          : NaN;
                      if (
                        typeof hw === "number" &&
                        Number.isFinite(maxH) &&
                        maxH > 0
                      ) {
                        const w = Math.round(hw * 10) / 10;
                        const mx = Math.round(maxH * 10) / 10;
                        return `${w}h/${mx}h`;
                      }
                      if (typeof hw === "number")
                        return `${Math.round(hw * 10) / 10}h`;
                      if (assignment.hours) return String(assignment.hours);
                      if (assignment.employee?.hours)
                        return String(assignment.employee.hours);
                      return "0h/0h";
                    })()}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Payer</p>
                  <p className="font-medium">
                    {assignment.employee?.payer_name ||
                      assignment.employee?.payer?.name ||
                      entry?.payer_name ||
                      "—"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Assignment Status</p>
                  <p className="font-medium capitalize">
                    {formatShiftUiStatusLabel(shiftAssignmentStatus)}
                  </p>
                </div>
                </div>
              </div>
            ))}
          </div>
        )}

      {/* If there are no REAL assignments (only unassigned slots) OR we're viewing an unassigned slot, show Unassigned status like the reference UI */}
      {(!hasRealAssignedStaff ||
        realAssignments.length === 0 ||
        entry?._isUnassignedSlotView) && (
        <div className="border-t pt-4 mb-6">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-medium text-gray-900">Assignment</h3>
          </div>
          <div className="text-xs">
            <p className="text-gray-500">Assignment Status</p>
            <p className="font-medium capitalize">
              {formatShiftUiStatusLabel(shiftAssignmentStatus)}
            </p>
          </div>
        </div>
      )}

      {/* Staff Tabs: Temp Staff (default) and Outsourced Agency */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-4 border-b border-gray-200">
            <button
              className={`pb-2 text-sm ${selectedStaffTab === "temp" ? "text-red-600 border-b-2 border-red-600" : "text-gray-600 border-b-2 border-transparent hover:text-red-600"}`}
              onClick={() => setSelectedStaffTab("temp")}
            >
              Temp Staff
            </button>
            {(!hasRealAssignedStaff ||
              realAssignments.length === 0 ||
              entry?._isUnassignedSlotView) && (
              <button
                className={`pb-2 text-sm ${selectedStaffTab === "outsourced" ? "text-red-600 border-b-2 border-red-600" : "text-gray-600 border-b-2 border-transparent hover:text-red-600"}`}
                onClick={() => setSelectedStaffTab("outsourced")}
              >
                Outsourced Agency
              </button>
            )}
          </div>
        </div>

        {/* Temp Staff Tab Content */}
        {selectedStaffTab === "temp" && (
          <>
            <p className="text-xs text-gray-500 mb-4">
              {(() => {
                const shiftObj = entry?.rawShift || entry || shift || {};
                const divName =
                  shiftObj?.division_name || shiftObj?.divisionName || "";
                const posName = (
                  panelPositionLabel ||
                  entry?.position ||
                  shiftObj?.positionName ||
                  shiftObj?.position_name ||
                  ""
                )
                  .toString()
                  .trim();
                const namedSkills = (shiftObj?.required_skill_names || "")
                  .toString()
                  .trim();
                const genericPos =
                  !posName ||
                  /^general\s*position$/i.test(posName);
                let skillsPhrase = "";
                if (namedSkills) {
                  skillsPhrase = ` and with skills: ${namedSkills}`;
                } else if (!genericPos) {
                  skillsPhrase = ` and with skills: ${posName}`;
                }
                return `Showing Any Temp Staff${divName ? ` in the ${divName} division` : ""}${skillsPhrase}`;
              })()}
            </p>

            {/* Search and Actions */}
            <div className="flex items-center mb-3">
              <div className="flex-1 relative">
                <FaSearch
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={16}
                />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-red-500"
                />
              </div>
              <div className="flex items-center gap-1 ml-1">
                <button
                  className="p-2 hover:bg-gray-50 rounded"
                  onClick={() => {
                    setFilterPanelMode("temp");
                    setShowFilterPanel(true);
                  }}
                >
                  <FaSlidersH size={16} className="text-gray-600" />
                </button>
                <AtlasRefreshIconButton
                  onClick={() => refreshTempStaffTable()}
                  useToolbarButton={false}
                  buttonClassName="p-2 hover:bg-gray-50 rounded"
                  iconClassName="text-gray-600"
                  iconSize={16}
                />
              </div>
              <button
                onClick={() => setShowConfirmCreate(true)}
                className="px-4 py-2 bg-red-500 text-white rounded text-sm font-medium hover:bg-red-600 ml-3"
              >
                CREATE
              </button>
            </div>

            {/* Active Filter Chips + Ignore Availability toggle chip */}
            <div className="flex flex-wrap gap-1 mb-2">
              {/* Type chips */}
              {appliedFilters.types.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 border border-gray-300 rounded-full text-xs text-gray-700"
                >
                  <button
                    className="hover:text-red-600"
                    onClick={() => {
                      setFilterPanelMode("temp");
                      setFilterFocusField("type");
                      setShowFilterPanel(true);
                    }}
                  >
                    Type: {t}
                  </button>
                  <button
                    className="ml-0.5 text-gray-500 hover:text-gray-800"
                    onClick={() =>
                      setAppliedFilters((prev) => ({
                        ...prev,
                        types: prev.types.filter((x) => x !== t),
                      }))
                    }
                  >
                    ×
                  </button>
                </span>
              ))}

              {/* Ignore availability & requirements — always visible toggle chip */}
              <button
                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border transition-colors select-none ${
                  appliedFilters.ignoreAvailability
                    ? "bg-blue-600 border-blue-600 text-white hover:bg-blue-700"
                    : "bg-white border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700"
                }`}
                onClick={() =>
                  setAppliedFilters((prev) => ({
                    ...prev,
                    ignoreAvailability: !prev.ignoreAvailability,
                  }))
                }
                title="When on: show all temp staff (skip shift conflicts, skills, and personnel-calendar confirmed-availability filter)."
              >
                {/* Tick/cross indicator */}
                <span
                  className={`w-3 h-3 rounded-sm border flex items-center justify-center flex-shrink-0 ${
                    appliedFilters.ignoreAvailability
                      ? "bg-white border-white"
                      : "bg-white border-gray-400"
                  }`}
                >
                  {appliedFilters.ignoreAvailability && (
                    <svg
                      viewBox="0 0 10 8"
                      width="8"
                      height="8"
                      fill="none"
                      stroke="#2563eb"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="1,4 4,7 9,1" />
                    </svg>
                  )}
                </span>
                Ignore availability &amp; requirements
              </button>
            </div>

            {/* Results Info — sortBy and direction each have their own inline popup */}
            <div className="relative mb-2 flex items-center flex-wrap gap-x-1">
              <span className="text-xs text-gray-500">
                Showing{" "}
                <span className="text-red-500">
                  {(searchTerm.trim() ? filteredStaff.length : staffTotalCount) === 0
                    ? 0
                    : (staffPage - 1) * STAFF_PAGE_SIZE + 1}
                  –{Math.min(
                    staffPage * STAFF_PAGE_SIZE,
                    searchTerm.trim() ? filteredStaff.length : staffTotalCount,
                  )}
                </span>{" "}
                of {searchTerm.trim() ? filteredStaff.length : staffTotalCount} results sorted by
              </span>

              {/* Sort By popup */}
              <div className="relative inline-block">
                <button
                  className="text-xs text-red-500 hover:underline focus:outline-none"
                  onClick={() => {
                    setShowSortByDropdown((v) => !v);
                    setShowSortDropdown(false);
                  }}
                >
                  {appliedFilters.sortBy}
                </button>
                {showSortByDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowSortByDropdown(false)}
                    />
                    <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded shadow-lg z-50 min-w-[180px]">
                      {[
                        "Preference",
                        "Proximity",
                        "Utilization",
                        "Staff First Name",
                        "Staff Last Name",
                      ].map((opt) => (
                        <button
                          key={opt}
                          className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 ${appliedFilters.sortBy === opt ? "font-semibold text-gray-900" : "text-gray-700"}`}
                          onClick={() => {
                            setAppliedFilters((prev) => ({
                              ...prev,
                              sortBy: opt,
                            }));
                            setShowSortByDropdown(false);
                          }}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <span className="text-xs text-gray-500">,</span>

              {/* Direction popup */}
              <div className="relative inline-block">
                <button
                  className="text-xs text-red-500 hover:underline focus:outline-none"
                  onClick={() => {
                    setShowSortDropdown((v) => !v);
                    setShowSortByDropdown(false);
                  }}
                >
                  {appliedFilters.sortDirection}
                </button>
                {showSortDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowSortDropdown(false)}
                    />
                    <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded shadow-lg z-50 min-w-[160px]">
                      {["Ascending", "Descending"].map((dir) => (
                        <button
                          key={dir}
                          className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 ${appliedFilters.sortDirection === dir ? "font-semibold text-gray-900" : "text-gray-700"}`}
                          onClick={() => {
                            setAppliedFilters((prev) => ({
                              ...prev,
                              sortDirection: dir,
                            }));
                            setShowSortDropdown(false);
                          }}
                        >
                          {dir}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Staff Table */}
            <div className="border rounded">
              {/* overscroll-y-contain: this table's own vertical scroll sits inside
                  RightPanel's full-height scroll (.right-panel-content). Without
                  containment, scrolling to the bottom row here chains into the outer
                  panel once this list runs out of scroll room -- on touch devices
                  that shift happens mid-tap, so the last row's tap target moves out
                  from under your finger right as you try to pick it. */}
              <div className="max-h-[520px] overflow-y-auto overflow-x-auto overscroll-y-contain">
                <table className="w-full text-sm keep-table-layout">
                  <thead className="bg-gray-50 text-left text-xs text-gray-500">
                    <tr>
                      <th className="px-3 py-2 font-medium">
                        Temp Staff
                      </th>
                      <th className="px-3 py-2 font-medium">Type</th>
                      <th className="px-3 py-2 font-medium text-center">
                        Distance
                      </th>
                      <th className="px-3 py-2 font-medium text-center">
                        Utilization
                        {appliedFilters.sortBy === "Utilization"
                          ? appliedFilters.sortDirection === "Ascending"
                            ? " ↑"
                            : " ↓"
                          : ""}
                      </th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStaff.map((staff, i) => {
                      const payRateLabel = formatTempStaffPayRateFromApi(
                        staff,
                        panelShiftPositionMeta.positionId,
                      );
                      return (
                        <tr key={i} className="border-t">
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <StaffAvatarCircle
                              employee={{
                                id: staff.id,
                                account_id: staff.id,
                                name: staff.name,
                              }}
                              className="w-8 h-8"
                              textClassName="text-xs font-medium text-gray-700"
                            />
                            <div className="min-w-0">
                              <span
                                className="block cursor-pointer truncate hover:text-red-600 hover:underline"
                                onClick={() =>
                                  handleOpenPersonnelModal({
                                    id: staff.id,
                                    account_id: staff.id,
                                    name: staff.name,
                                    type: staff.type,
                                    initials: staff.name
                                      .split(" ")
                                      .map((n) => n[0])
                                      .join(""),
                                    phone: staff.phone,
                                  })
                                }
                              >
                                {staff.name}
                              </span>
                              {payRateLabel ? (
                                <div className="text-xs text-gray-400 mt-0.5">
                                  {payRateLabel}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          {staff.type}
                        </td>
                        <td className="px-3 py-2 text-center align-middle tabular-nums">
                          {staff.proximity || "—"}
                        </td>
                        <td className="px-3 py-2 text-center align-middle">
                          <div className="font-medium text-gray-900 tabular-nums">
                            {staff.utilization || "0%"}
                          </div>
                          <div className="text-xs text-gray-400 tabular-nums">
                            {staff.hours || "0h/0h"}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <ActionDropdown
                            align="right"
                            actions={[
                              {
                                label: "Assign",
                                className: isClosedWeek
                                  ? "text-gray-400 cursor-not-allowed"
                                  : "text-gray-700",
                                onClick: isClosedWeek
                                  ? undefined
                                  : async () => {
                                      await handleAssignStaff(staff);
                                      await refreshParentSchedule();
                                      setTimeout(() => {
                                        void refreshTempStaffTable();
                                      }, 250);
                                    },
                              },
                              {
                                label: "View Profile",
                                onClick: () => {
                                  handleOpenPersonnelModal({
                                    id: staff.id,
                                    account_id: staff.id,
                                    name: staff.name,
                                    type: staff.type,
                                    initials: staff.name
                                      .split(" ")
                                      .map((n) => n[0])
                                      .join(""),
                                    phone: staff.phone,
                                  });
                                },
                              },
                            ]}
                          />
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination controls */}
            {staffTotalPages > 1 && (
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-500">
                  Page {staffPage} of {staffTotalPages}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    disabled={staffPage <= 1 || staffLoading}
                    className={`px-2 py-1 text-xs rounded border ${staffPage <= 1 || staffLoading ? "border-gray-200 text-gray-300 cursor-not-allowed" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
                    onClick={() => {
                      const newPage = staffPage - 1;
                      setStaffPage(newPage);
                      doSearch(searchTerm.trim(), newPage);
                    }}
                  >
                    ‹ Prev
                  </button>

                  {/* Page number buttons — show up to 5 around current page */}
                  {Array.from({ length: staffTotalPages }, (_, i) => i + 1)
                    .filter(
                      (p) =>
                        p === 1 ||
                        p === staffTotalPages ||
                        Math.abs(p - staffPage) <= 1,
                    )
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push("…");
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      p === "…" ? (
                        <span
                          key={`ellipsis-${idx}`}
                          className="px-1 text-xs text-gray-400"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          disabled={staffLoading}
                          className={`px-2 py-1 text-xs rounded border ${p === staffPage ? "bg-red-500 text-white border-red-500" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
                          onClick={() => {
                            setStaffPage(p);
                            doSearch(searchTerm.trim(), p);
                          }}
                        >
                          {p}
                        </button>
                      ),
                    )}

                  <button
                    disabled={staffPage >= staffTotalPages || staffLoading}
                    className={`px-2 py-1 text-xs rounded border ${staffPage >= staffTotalPages || staffLoading ? "border-gray-200 text-gray-300 cursor-not-allowed" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
                    onClick={() => {
                      const newPage = staffPage + 1;
                      setStaffPage(newPage);
                      doSearch(searchTerm.trim(), newPage);
                    }}
                  >
                    Next ›
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Outsourced Agency Tab Content */}
        {selectedStaffTab === "outsourced" &&
          (!hasRealAssignedStaff ||
            realAssignments.length === 0 ||
            entry?._isUnassignedSlotView) && (
            <div>
              <p className="text-xs text-gray-500 mb-4">
                Showing 1-{outsourcedPayers.length || 0} of{" "}
                {outsourcedPayers.length || 0} results sorted by Outsourced
                Agency, Ascending
              </p>

              {/* Search and Actions for Outsourced tab */}
              <div className="flex items-center mb-3">
                <div className="flex-1 relative">
                  <FaSearch
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    size={16}
                  />
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-red-500"
                  />
                </div>
                <div className="flex items-center gap-1 ml-1">
                  <button
                    className="p-2 hover:bg-gray-50 rounded"
                    onClick={() => {
                      setFilterPanelMode("outsourced");
                      setShowFilterPanel(true);
                    }}
                  >
                    <FaSlidersH size={16} className="text-gray-600" />
                  </button>
                  <AtlasRefreshIconButton
                    onClick={() => fetchOutsourcedPayers()}
                    useToolbarButton={false}
                    buttonClassName="p-2 hover:bg-gray-50 rounded"
                    iconClassName="text-gray-600"
                    iconSize={16}
                  />
                </div>
              </div>

              <div className="border rounded">
                <div className="max-h-56 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs text-gray-500">
                      <tr>
                        <th className="px-3 py-2 font-medium">
                          Outsourced Agency
                        </th>
                        <th className="px-3 py-2 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const sorted = (outsourcedPayers || [])
                          .slice()
                          .sort((a, b) => {
                            const aName = (a.name || "").toLowerCase();
                            const bName = (b.name || "").toLowerCase();
                            if (aName < bName)
                              return outsourcedSortDirection === "Ascending"
                                ? -1
                                : 1;
                            if (aName > bName)
                              return outsourcedSortDirection === "Ascending"
                                ? 1
                                : -1;
                            return 0;
                          });
                        return sorted.map((p, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                                  <span className="text-xs font-medium text-gray-700">
                                    {(p.name || "")
                                      .split(" ")
                                      .map((n) => n[0])
                                      .join("")}
                                  </span>
                                </div>
                                <span className="text-gray-900">{p.name}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <button
                                disabled={!!isClosedWeek}
                                className={`px-3 py-1 border rounded text-sm ${isClosedWeek ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200" : "hover:bg-gray-50"}`}
                                onClick={() => {
                                  if (isClosedWeek) return;
                                  if (typeof onOpenShiftModal === "function") {
                                    onOpenShiftModal(
                                      buildShiftPayloadForJobsModal(),
                                      entry,
                                    );
                                  }
                                }}
                              >
                                ASSIGN
                              </button>
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
      </div>

      {/* Staff Filter Panel */}
      <StaffFilterPanel
        isOpen={showFilterPanel}
        onClose={() => {
          setShowFilterPanel(false);
          setFilterFocusField(null);
        }}
        onApplyFilters={(filters) => {
          handleApplyFilters(filters, filterPanelMode);
          setShowFilterPanel(false);
          setFilterFocusField(null);
        }}
        mode={filterPanelMode}
        initialSortBy={
          filterPanelMode === "outsourced"
            ? outsourcedSortBy
            : appliedFilters.sortBy
        }
        initialSortDirection={
          filterPanelMode === "outsourced"
            ? outsourcedSortDirection
            : appliedFilters.sortDirection
        }
        initialSelectedTypes={appliedFilters.types}
        focusField={filterFocusField}
      />
      {/* Edit Note Modal (Note to Temp Staff) */}
      {showEditNoteModal &&
        (() => {
          const modal = (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-[50400] flex items-center justify-center p-4"
              onClick={() => setShowEditNoteModal(false)}
            >
              <div
                className="bg-white rounded-lg shadow-xl w-full max-w-md p-4"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-base font-semibold">
                    Note to Temp Staff
                  </h3>
                  <button
                    onClick={() => setShowEditNoteModal(false)}
                    className="text-gray-500 hover:text-gray-700 text-sm"
                  >
                    ✖
                  </button>
                </div>
                <div className="py-2">
                  <div>
                    <input
                      type="text"
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-2 text-sm"
                      placeholder="Enter note"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
                  <button
                    onClick={() => setShowEditNoteModal(false)}
                    className="px-3 py-1 text-sm border rounded hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      setSavingNote(true);
                      const res = await saveNoteToBackend("staff");
                      setSavingNote(false);
                      if (res && res.ok) {
                        setLocalNote(noteDraft || null);
                        setShowEditNoteModal(false);
                        if (typeof onShiftNotesSaved === "function") {
                          onShiftNotesSaved();
                        }
                      } else {
                        showError(
                          "Failed to save note: " +
                            (res && res.message
                              ? res.message
                              : "Unknown error"),
                        );
                      }
                    }}
                    className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    {savingNote ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          );
          if (typeof document !== "undefined")
            return ReactDOM.createPortal(modal, document.body);
          return modal;
        })()}

      {/* Edit Admin Note Modal */}
      {showEditAdminModal &&
        (() => {
          const modal = (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-[50400] flex items-center justify-center p-4"
              onClick={() => setShowEditAdminModal(false)}
            >
              <div
                className="bg-white rounded-lg shadow-xl w-full max-w-md p-4"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-base font-semibold">Admin Note</h3>
                  <button
                    onClick={() => setShowEditAdminModal(false)}
                    className="text-gray-500 hover:text-gray-700 text-sm"
                  >
                    ✖
                  </button>
                </div>
                <div className="py-2">
                  <div>
                    <input
                      type="text"
                      value={adminDraft}
                      onChange={(e) => setAdminDraft(e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-2 text-sm"
                      placeholder="Enter admin note"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
                  <button
                    onClick={() => setShowEditAdminModal(false)}
                    className="px-3 py-1 text-sm border rounded hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      setSavingAdmin(true);
                      const res = await saveNoteToBackend("admin");
                      setSavingAdmin(false);
                      if (res && res.ok) {
                        setLocalAdminNote(adminDraft || null);
                        setShowEditAdminModal(false);
                        if (typeof onShiftNotesSaved === "function") {
                          onShiftNotesSaved();
                        }
                      } else {
                        showError(
                          "Failed to save admin note: " +
                            (res && res.message
                              ? res.message
                              : "Unknown error"),
                        );
                      }
                    }}
                    className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    {savingAdmin ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          );
          if (typeof document !== "undefined")
            return ReactDOM.createPortal(modal, document.body);
          return modal;
        })()}

      {showConfirmCreate &&
        (() => {
          const confirmModal = (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-[50300] flex items-center justify-center p-4"
              onClick={() => setShowConfirmCreate(false)}
            >
              <div
                className="bg-white rounded-lg shadow-xl w-full max-w-md p-4"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-base font-semibold">Confirm</h3>
                  <button
                    onClick={() => setShowConfirmCreate(false)}
                    className="text-gray-500 hover:text-gray-700 text-sm"
                  >
                    ✖
                  </button>
                </div>
                <div className="py-4 text-sm text-gray-700">
                  By continuing, you will create a Staff profile with the
                  required shift gender, skills and certifications. Edits to
                  these profile parameters after they have been set may result
                  in the profile not being available for the current shift
                  assignment.
                </div>
                <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
                  <button
                    onClick={() => setShowConfirmCreate(false)}
                    className="px-3 py-1 text-sm border rounded hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowConfirmCreate(false);
                      setShowCreateTempModal(true);
                    }}
                    className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Ok
                  </button>
                </div>
              </div>
            </div>
          );
          if (typeof document !== "undefined")
            return ReactDOM.createPortal(confirmModal, document.body);
          return confirmModal;
        })()}

      {showCreateTempModal && (
        <AssignmentDetailModal
          isOpen={true}
          onClose={() => setShowCreateTempModal(false)}
          assignment={null}
          openCreate={true}
        />
      )}
      {showAssignSimilarModal && (
        <AssignToSimilarModal
          open={showAssignSimilarModal}
          onClose={() => setShowAssignSimilarModal(false)}
          originalShift={
            modalOriginalShift ||
            resolveSlotRawShiftForPanel(entry, assignment, shift) ||
            entry?.rawShift ||
            entry ||
            shift
          }
          staffAccountId={staffAccountId}
          onDone={() => {
            if (searchTimer.current) clearTimeout(searchTimer.current);
            searchTimer.current = setTimeout(
              () => doSearch(searchTerm.trim(), staffPage),
              200,
            );
            void refreshParentSchedule();
            setTimeout(() => refreshTempStaffTable(), 400);
          }}
        />
      )}

      <CarpoolEditModal
        isOpen={carpoolModalOpen}
        onClose={() => setCarpoolModalOpen(false)}
        initialName={assignmentFlags.carpoolName}
        initialNote={assignmentFlags.carpoolNote}
        readOnly={readOnly || isClosedWeek}
        saving={carpoolSaving}
        onSave={handleCarpoolSave}
      />

      {scheduleUnassignOpen &&
        typeof document !== "undefined" &&
        ReactDOM.createPortal(
          <UnassignShiftDialog
            open={scheduleUnassignOpen}
            suppressSuccess={!!scheduleUnassignPayload.silentUnassignSuccess}
            onClose={() => {
              pendingAssignAfterUnassignRef.current = null;
              setScheduleUnassignOpen(false);
              setScheduleUnassignPayload((p) => ({
                ...p,
                silentUnassignSuccess: false,
              }));
            }}
            shiftId={scheduleUnassignPayload.shiftId}
            assignedAccountId={scheduleUnassignPayload.accountId}
            staffDisplayName={scheduleUnassignPayload.staffDisplayName}
            shiftStartAt={scheduleUnassignPayload.startAt}
            shiftEndAt={scheduleUnassignPayload.endAt}
            onSuccess={async () => {
              const pending = pendingAssignAfterUnassignRef.current;
              pendingAssignAfterUnassignRef.current = null;
              if (pending) {
                try {
                  await performAssignRequest(pending);
                } catch (e) {
                  console.error(e);
                  showError(
                    e.message || "Failed to assign after unassigning",
                  );
                }
              }
              await refreshParentSchedule();
              // Staff list reload is driven by useEffect (panelShiftId / hasRealAssignedStaff).
            }}
          />,
          document.body,
        )}
    </div>
  );
};

export default ShiftDetailPanel;
